import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DollarSign,
  TrendingUp,
  Users,
  CheckCircle,
  Clock,
  AlertTriangle,
  BarChart3,
  PieChart,
  Send,
  Sparkles,
  ArrowUp,
  ArrowDown,
  Calendar,
  Target,
  X,
  Phone,
  MessageCircle,
  Timer,
  History,
  User,
  ExternalLink,
  PlusCircle,
  Edit,
  Trash2,
  FileText,
  Sun,
  Moon,
  ChevronRight,
  ChevronDown,
  MapPin
  , Banknote
} from 'lucide-react';
import { getWhatsappLink, formatDate } from '../../utils/helpers';
import {
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell,
  AreaChart,
  Area,
  BarChart,
  Bar,
  Legend,
} from 'recharts';
import { STAGES, STAGE_COLORS, COLORS, LOSS_REASONS, LOSS_REASON_BY_ID, DISQUALIFYING_REASONS } from '../../lib/constants';
import { safeAmount, formatCurrency, isWonStage } from '../../utils/helpers';
import { handleOwnerQuery } from '../../services/ai';
import { useLanguage } from '../../contexts/LanguageContext';
import { useSocket } from '../../hooks/useSocket';
import { auditService, bookingService, conversationService, settingsService, fetchWithAuth, API_BASE_URL } from '../../services/api';
import { bookingService as banquetBookingService } from '../../services/booking.service';
import { Skeleton, DatePicker, Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem } from '../ui';
import { BookingDetailModal } from '../modals';



// Module-level cache to prevent flashing loading states on re-mounts
let cachedAuditLogs = null;
let cachedBookings = null;
let cachedConversations = null;
const cachedFinancialsMap = new Map();
// Full banquet booking history for the Demand view — one fetch per app session.
let cachedBanquetAllRows = null;
// §4 ad-spend ledger — cached per app session, written back via settings API.
let cachedAdSpend = null;

// §4 spend ledger editor. Meta's column is read-only — the sync owns it;
// humans type only the money Meta cannot see: Google, one-offs, and the fee.
function SpendEditor({ open, onClose, ledger, onSaved }) {
  const { t } = useLanguage();
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  useEffect(() => {
    if (open) {
      setNote('');
      setDraft(JSON.parse(JSON.stringify(ledger || { months: {} })));
    }
  }, [open, ledger]);
  if (!open || !draft) return null;

  const now = new Date();
  const keys = Array.from({ length: 13 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const setVal = (m, src, v) => {
    const months = { ...(draft.months || {}) };
    months[m] = { ...(months[m] || {}) };
    const num = Number(v);
    if (!v || isNaN(num) || num <= 0) delete months[m][src];
    else months[m][src] = Math.round(num);
    setDraft({ ...draft, months });
  };
  const save = async () => {
    setBusy(true);
    try {
      const saved = await settingsService.update('ad_spend', draft);
      onSaved(saved);
      onClose();
    } catch (e) { setNote(e.message || 'Save failed'); }
    setBusy(false);
  };
  const syncMeta = async () => {
    setBusy(true);
    setNote('');
    try {
      const r = await fetchWithAuth(`${API_BASE_URL}/marketing/sync-meta-spend`, { method: 'POST' });
      if (r.status === 404) {
        setNote(t('syncNotDeployed') || 'Sync endpoint not deployed yet — one click after the next deploy.');
      } else {
        const j = await r.json();
        if (!r.ok || j.status === false) {
          setNote(j.message || 'Sync failed');
        } else {
          const fresh = await settingsService.get('ad_spend');
          onSaved(fresh);
          setDraft(JSON.parse(JSON.stringify(fresh || { months: {} })));
          setNote(j.message || 'Synced');
        }
      }
    } catch (e) { setNote(e.message || 'Sync failed'); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-bold text-gray-900">{t('spendLedger') || 'Ad Spend Ledger'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>
        <div className="p-4 overflow-y-auto space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-xs font-bold text-gray-500 uppercase">{t('agencyFee') || 'Agency fee / month'}</label>
            <input
              type="number"
              className="border rounded-lg p-2 text-sm w-36 text-right"
              value={draft.agencyFeeMonthly || ''}
              placeholder="150000"
              onChange={(e) => setDraft({ ...draft, agencyFeeMonthly: Number(e.target.value) || 0 })}
            />
            <button onClick={syncMeta} disabled={busy} className="ml-auto px-3 py-2 text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-50 transition-colors">
              {t('syncMeta') || 'Sync Meta spend now'}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left">
                  <th className="px-2 py-2 text-xs font-semibold text-gray-600 uppercase">{t('month') || 'Month'}</th>
                  <th className="px-2 py-2 text-xs font-semibold text-gray-600 uppercase text-right">Meta (auto)</th>
                  <th className="px-2 py-2 text-xs font-semibold text-gray-600 uppercase text-right">Google Ads</th>
                  <th className="px-2 py-2 text-xs font-semibold text-gray-600 uppercase text-right">{t('otherSpend') || 'Other'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {keys.map((m) => (
                  <tr key={m}>
                    <td className="px-2 py-1.5 font-medium text-gray-700">{m}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-gray-500">
                      {draft.months?.[m]?.['Meta Lead Gen'] ? Number(draft.months[m]['Meta Lead Gen']).toLocaleString() : '—'}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <input type="number" className="border rounded p-1 text-right text-sm w-28" value={draft.months?.[m]?.['Google Ads'] ?? ''} onChange={(e) => setVal(m, 'Google Ads', e.target.value)} />
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <input type="number" className="border rounded p-1 text-right text-sm w-28" value={draft.months?.[m]?.['Other'] ?? ''} onChange={(e) => setVal(m, 'Other', e.target.value)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {note && <p className="text-xs text-amber-700">{note}</p>}
          <p className="text-[11px] text-gray-400">
            {t('spendLedgerNote') || 'Meta fills itself via sync. Type only what Meta cannot see: Google, one-off channels, and the fee.'}
          </p>
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">{t('cancel') || 'Cancel'}</button>
          <button onClick={save} disabled={busy} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50">
            {t('save') || 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OwnerDashboard({ leads = [], onShowRevenue, fetchAnalytics, analyticsLoading }) {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  // Rolling windows, not calendar-to-date: "This Month" on the 2nd of a month
  // is a 48-hour window and made every card read zero. Default is the last 30
  // days — the question the dashboard actually opens to answer.
  const [dateRange, setDateRange] = useState('30d');
  const fmtDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const [customFrom, setCustomFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 29); return fmtDay(d); });
  const [customTo, setCustomTo] = useState(() => fmtDay(new Date()));
  const [dashboardLeads, setDashboardLeads] = useState(leads || []);
  const [analyticsData, setAnalyticsData] = useState(null);

  const [banquetFinancials, setBanquetFinancials] = useState({ booked: 0, collected: 0, outstanding: 0, count: 0 });
  // Money cards: which date the period filters on. 'booking' = when the sale
  // was made (BMS order_date — today's behaviour); 'event' = when the function
  // happens (function_date) — "what's on the books FOR this period".
  const [revenueAxis, setRevenueAxis] = useState('booking');

  // All banquet bookings (every year) for the Demand view. The financials API
  // filters on order_date, so a since-2020 range returns the full booking book —
  // grouping by function_date happens client-side.
  const [banquetAllRows, setBanquetAllRows] = useState(cachedBanquetAllRows || []);
  useEffect(() => {
    if (cachedBanquetAllRows) return;
    const now = new Date();
    const to = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    banquetBookingService.fetchCrmBookingFinancials('2020-01-01', to)
      .then((res) => {
        cachedBanquetAllRows = res?.data || [];
        setBanquetAllRows(cachedBanquetAllRows);
      })
      .catch(() => { /* demand card degrades to inquiries-only */ });
  }, []);

  // Lifted to component scope: used both by the financials fetch below and by
  // the Finance drill-down links on the money cards, so the list you land on
  // covers exactly the period the card was showing.
  const getDateRangeBounds = (range) => {
      const now = new Date();
      let from, to;
      switch (range) {
        case '90d': {
          from = new Date(now);
          from.setDate(from.getDate() - 89);
          to = new Date(now);
          break;
        }
        case 'ytd': {
          from = new Date(now.getFullYear(), 0, 1);
          to = new Date(now);
          break;
        }
        case '12m': {
          from = new Date(now);
          from.setDate(from.getDate() - 364);
          to = new Date(now);
          break;
        }
        case 'all': {
          from = new Date(2000, 0, 1);
          to = new Date(now);
          break;
        }
        case 'custom': {
          // The pickers own these strings; fall back to last-30-days shape if
          // one is cleared mid-edit so nothing downstream sees an empty bound.
          if (customFrom && customTo) return { from: customFrom, to: customTo };
          from = new Date(now);
          from.setDate(from.getDate() - 29);
          to = new Date(now);
          break;
        }
        default: { // 30d
          from = new Date(now);
          from.setDate(from.getDate() - 29);
          to = new Date(now);
        }
      }
      return { from: fmtDay(from), to: fmtDay(to) };
  };

  // Money cards open the Finance screen scoped to the same period and status,
  // rather than duplicating that table in a modal. Finance already has search,
  // sorting, amount filters and pagination.
  const openInFinance = (status) => {
    const { from, to } = getDateRangeBounds(dateRange);
    navigate(`/finance?from=${from}&to=${to}&status=${status}`);
  };

  useEffect(() => {

    const fetchFinancials = async () => {
      try {
        const { from, to } = getDateRangeBounds(dateRange);
        const cacheKey = `${from}_${to}`;
        if (cachedFinancialsMap.has(cacheKey)) {
          setBanquetFinancials(cachedFinancialsMap.get(cacheKey));
        }
        const result = await banquetBookingService.fetchCrmBookingFinancials(from, to);
        if (result?.status && Array.isArray(result.data)) {
          let booked = 0;
          let collected = 0;
          let outstanding = 0;
          result.data.forEach(l => {
            const financialSummary = l.financial_summary || {};
            const totalValue = safeAmount(financialSummary.invoice_net_amount || 0);
            const totalPaid = safeAmount(financialSummary.total_received_amount || 0);
            const totalDue = safeAmount(financialSummary.balance_amount ?? Math.max(0, totalValue - totalPaid));
            booked += totalValue;
            collected += totalPaid;
            outstanding += totalDue;
          });
          // count: the BMS returns bookings PLACED in the requested window
          // (evidence: bookings for 2027 events come back when to_date is
          // mid-2026, so the API filters on order_date, not event date). Row
          // count therefore IS "bookings taken this period" — complete,
          // including walk-ins and direct BMS entries that never had a CRM lead.
          const newVals = { booked, collected, outstanding, count: result.data.length };
          cachedFinancialsMap.set(cacheKey, newVals);
          setBanquetFinancials(newVals);
        }
      } catch (err) {
        console.error('Error fetching banquet financials:', err);
      }
    };
    fetchFinancials();
  }, [dateRange, customFrom, customTo]);

  // Event-axis money: the same three figures keyed on function_date instead of
  // order_date — "what's on the books FOR this period's events, whenever it was
  // sold". Computed from the full booking book already cached for the Demand
  // and YoY cards (bookings placed 2020→today, which by definition contains
  // every future event ever sold), so flipping the toggle costs no API call.
  const eventFinancials = useMemo(() => {
    const { from, to } = getDateRangeBounds(dateRange);
    let booked = 0, collected = 0, outstanding = 0, count = 0;
    (banquetAllRows || []).forEach((b) => {
      const fd = String(b.function_date || '').slice(0, 10);
      if (!fd || fd < from || fd > to) return;
      const fs = b.financial_summary || {};
      const val = safeAmount(fs.invoice_net_amount || 0);
      const paid = safeAmount(fs.total_received_amount || 0);
      booked += val;
      collected += paid;
      outstanding += safeAmount(fs.balance_amount ?? Math.max(0, val - paid));
      count += 1;
    });
    return { booked, collected, outstanding, count };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [banquetAllRows, dateRange, customFrom, customTo]);
  const fin = revenueAxis === 'event' ? eventFinancials : banquetFinancials;


  const [activeModal, setActiveModal] = useState(null); // 'leads', 'conversion', 'stale', 'ytd', 'source', 'funnel', 'summary'
  const [selectedSource, setSelectedSource] = useState(null);
  // §3 month-trend source selector (Marketing Performance card)
  const [marketingSource, setMarketingSource] = useState('Meta Lead Gen');
  // §4 spend ledger + editor; §5 year-on-year month selector (December default)
  const [adSpend, setAdSpend] = useState(cachedAdSpend);
  const [showSpendEditor, setShowSpendEditor] = useState(false);
  const [yoyMonth, setYoyMonth] = useState('12');
  useEffect(() => {
    if (cachedAdSpend) return;
    settingsService.get('ad_spend')
      .then((v) => { cachedAdSpend = v || { months: {} }; setAdSpend(cachedAdSpend); })
      .catch(() => { cachedAdSpend = { months: {} }; setAdSpend(cachedAdSpend); });
  }, []);
  const [selectedStage, setSelectedStage] = useState(null);
  const [visibleCount, setVisibleCount] = useState(50);
  const [summaryModalType, setSummaryModalType] = useState(null);
  const [selectedLeadDetail, setSelectedLeadDetail] = useState(null);

  // ── Sales Team date filter ─────────────────────────────────────────
  const getMonthDefaults = () => {
    const now = new Date();
    const y = now.getFullYear(), m = String(now.getMonth() + 1).padStart(2, '0');
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    return {
      from: `${y}-${m}-01`,
      to: `${y}-${m}-${String(lastDay).padStart(2, '0')}`,
    };
  };
  const monthDef = getMonthDefaults();
  const [summaryFrom, setSummaryFrom] = useState(monthDef.from);
  const [summaryTo, setSummaryTo] = useState(monthDef.to);
  const [recentAuditLogs, setRecentAuditLogs] = useState(cachedAuditLogs || []);
  const [logsLoading, setLogsLoading] = useState(!cachedAuditLogs);
  const [upcomingBookings, setUpcomingBookings] = useState(cachedBookings || []);
  const [bookingsLoading, setBookingsLoading] = useState(!cachedBookings);
  const [recentConversations, setRecentConversations] = useState(cachedConversations || []);
  const [conversationsLoading, setConversationsLoading] = useState(!cachedConversations);
  const [selectedBookingForModal, setSelectedBookingForModal] = useState(null);

  const { socket } = useSocket();

  // Fetch analytics from the scalable API whenever dateRange changes
  useEffect(() => {
    if (fetchAnalytics) {
      // Send explicit bounds: the analytics API only knows the legacy preset
      // names, but it honours startDate/endDate for any window. 'all' keeps
      // its dedicated server path (year-bucketed trend, no previous period).
      const { from, to } = getDateRangeBounds(dateRange);
      const params = dateRange === 'all' ? { dateRange } : { dateRange, startDate: from, endDate: to };
      fetchAnalytics(params).then(data => {
        if (data) setAnalyticsData(data);
      });
    }
  }, [dateRange, customFrom, customTo, fetchAnalytics]);

  // Keep dashboardLeads in sync with leads prop for drilldown modals
  useEffect(() => {
    if (leads && leads.length > 0) {
      setDashboardLeads(leads);
    }
  }, [leads]);

  // Fetch initial data & setup socket listeners
  useEffect(() => {
    let isMounted = true;

    const fetchInitialData = () => {
      // Background fetch without showing loading state if clicked back

      auditService.getAll({ limit: 5 })
        .then(res => {
          if (isMounted) {
            setRecentAuditLogs(res.data || []);
            cachedAuditLogs = res.data || [];
            setLogsLoading(false);
          }
        })
        .catch(err => {
          console.error('Error fetching audit logs:', err);
          if (isMounted) setLogsLoading(false);
        });

      banquetBookingService.fetchUpcomingBookings(5)
        .then(res => {
          if (isMounted) {
            setUpcomingBookings(res || []);
            cachedBookings = res || [];
            setBookingsLoading(false);
          }
        })
        .catch(err => {
          console.error('Error fetching upcoming bookings:', err);
          if (isMounted) setBookingsLoading(false);
        });

      conversationService.getAll({ limit: 5 })
        .then(res => {
          if (isMounted) {
            setRecentConversations(res.data || []);
            cachedConversations = res.data || [];
            setConversationsLoading(false);
          }
        })
        .catch(err => {
          console.error('Error fetching recent conversations:', err);
          if (isMounted) setConversationsLoading(false);
        });

      // No separate leads fetch — dashboardLeads is synced from the `leads` prop (App.jsx useLeads hook)
      // This avoids a redundant 13MB+ API call on every dashboard visit.
    };

    fetchInitialData();

    return () => {
      isMounted = false;
    };
  }, []);

  // Socket listeners for real-time updates
  useEffect(() => {
    if (!socket) return;

    const handleAuditLogCreated = (log) => {
      setRecentAuditLogs(prev => {
        // Prevent duplicate logs if they arrive through other means
        if (prev.some(l => l.id === log.id)) return prev;
        const updated = [log, ...prev].slice(0, 5);
        cachedAuditLogs = updated;
        return updated;
      });
    };

    const handleActivityAdded = () => {
      // Quietly refetch conversations when a new message/activity comes in
      conversationService.getAll({ limit: 5 }).then(response => {
        const newConvos = response.data || [];
        setRecentConversations(newConvos);
        cachedConversations = newConvos;
      }).catch(console.error);
    };

    const handleNotification = () => {
      // Quietly refetch bookings/conversations since notification can be for new leads/bookings
      banquetBookingService.fetchUpcomingBookings(5).then(res => {
        const newBookings = res || [];
        setUpcomingBookings(newBookings);
        cachedBookings = newBookings;
      }).catch(console.error);

      conversationService.getAll({ limit: 5 }).then(response => {
        const newConvos = response.data || [];
        setRecentConversations(newConvos);
        cachedConversations = newConvos;
      }).catch(console.error);
    };

    // Note: When lead stage changes to booked, backend might emit activity_added or notification,
    // which handles refetching the bookings automatically through handleNotification.

    socket.on('audit_log_created', handleAuditLogCreated);
    socket.on('activity_added', handleActivityAdded);
    socket.on('notification', handleNotification);

    return () => {
      socket.off('audit_log_created', handleAuditLogCreated);
      socket.off('activity_added', handleActivityAdded);
      socket.off('notification', handleNotification);
    };
  }, [socket]);


  // Map analytics API data to the shape expected by dashboard UI
  // All heavy computation is now done server-side in getLeadsAnalytics controller
  const metrics = useMemo(() => {
    const a = analyticsData;
    if (!a) {
      // Default empty state while loading
      return {
        totalLeads: 0, periodLeads: 0, activePeriodLeads: 0, bookedPeriodLeads: 0,
        previousPeriodLeads: 0, leadsBySource: {}, conversionBySource: {}, leadsByStage: {},
        employeeStats: {}, revenueInPeriod: 0, previousRevenue: 0, ytdRevenue: 0,
        conversionRate: 0, trendData: [], staleLeads: [], overduePayments: [], avgResponseTime: null,
        revenueGrowth: 0, _startOfPeriod: new Date(0)
      };
    }
    return {
      totalLeads: a.totalLeads || 0,
      periodLeads: a.periodLeads || 0,
      activePeriodLeads: (a.periodLeads || 0) - (a.bookedPeriodLeads || 0),
      bookedPeriodLeads: a.bookedPeriodLeads || 0,
      previousPeriodLeads: a.previousPeriodLeads || 0,
      leadsBySource: a.leadsBySource || {},
      conversionBySource: a.conversionBySource || {},
      leadsByStage: a.leadsByStage || {},
      employeeStats: a.employeeStats || {},
      revenueInPeriod: a.revenueInPeriod || 0,
      previousRevenue: a.previousRevenue || 0,
      ytdRevenue: a.ytdRevenue || 0,
      conversionRate: a.conversionRate || 0,
      trendData: a.trendData || [],
      staleLeads: a.staleLeads || [],
      drilldownLeads: a.drilldownLeads || [],
      ytdDrilldownLeads: a.ytdDrilldownLeads || [],
      bookedDrilldownLeads: a.bookedDrilldownLeads || [],
      overduePayments: [],
      avgResponseTime: a.avgResponseTime !== undefined ? a.avgResponseTime : null,
      revenueGrowth: a.revenueGrowth || 0,
      _startOfPeriod: dateRange === 'all'
        ? new Date(0)
        : new Date(getDateRangeBounds(dateRange).from + 'T00:00:00')
    };
  }, [analyticsData, dateRange, customFrom, customTo]);

  // Handle AI query
  const handleAiSubmit = async () => {
    if (!aiQuery.trim()) return;
    setAiLoading(true);
    const response = await handleOwnerQuery(aiQuery);
    setAiResponse(response);
    setAiLoading(false);
  };

  // Prepare chart data
  const sourceData = Object.entries(metrics.leadsBySource)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 6);

  // Denominator for the source percentages. `leadsBySource` is filtered to the
  // selected period, so the share must be measured against the period's own
  // total — not `metrics.totalLeads`, which is the all-time count and made
  // every source read 3% or 0%. Uses every source, not just the top 6 shown.
  const periodSourceTotal = Object.values(metrics.leadsBySource)
    .reduce((sum, v) => sum + (v || 0), 0);

  // A tentative booking is created when a lead reaches Site Visited (A-03/A-06).
  //
  // Counting leads *currently* at that stage undercounts badly — a tentative
  // that went on to Won or Lost stops being counted, and most of them do. The
  // `siteVisited` flag records that a visit happened and survives the lead
  // moving on, so it answers the actual question: how many tentative bookings
  // were made. (Some leads sit at Site Visited without the flag, so accept
  // either signal.) Scoped by lead creation date to match the cards above.
  //
  // Walk-ins are excluded: their flag is set at birth (the inquiry happened
  // inside the hall), but browsing in off the street is not a tentative
  // booking — counting them would drown the card in foot traffic.
  const { from: periodFrom, to: periodTo } = getDateRangeBounds(dateRange);
  const tentativeLeads = (dashboardLeads || []).filter((l) => {
    if (/walk/i.test(String(l.source || ''))) return false;
    const created = String(l.inquiryDate || l.createdAt || '').slice(0, 10);
    if (!created || created < periodFrom || created > periodTo) return false;
    return l.siteVisited === true || l.stage === 'Site Visited';
  });

  // "Booked This Period" filters strictly on bookedAt, which is only stamped at
  // the moment a lead becomes Won. Wins that predate that behaviour have no date
  // and so fall out of every period. Excluding them is correct — inventing a date
  // is what made these figures untrustworthy before — but it has to be visible,
  // otherwise the card silently under-reports. Run the banquet-system backfill
  // (maintenance/backfill-booking-dates) to recover the real dates.
  const winsWithoutBookingDate = (dashboardLeads || [])
    .filter((l) => isWonStage(l.stage) && !l.bookedAt).length;

  // ── Demand view (§6): of the inquiries received in the selected period,
  // which event months are they asking about? Confirmed banquet bookings for
  // the same months sit alongside, so demand reads against supply. Leads
  // without a usable event date are counted and disclosed, never silently
  // dropped; a horizon keeps junk dates (2001, 2030) from stretching the axis.
  const demand = useMemo(() => {
    const inPeriod = (dashboardLeads || []).filter((l) => {
      const created = String(l.inquiryDate || l.createdAt || '').slice(0, 10);
      return created && created >= periodFrom && created <= periodTo;
    });

    const now = new Date();
    const minKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const maxD = new Date(now.getFullYear(), now.getMonth() + 18, 1);
    const maxKey = `${maxD.getFullYear()}-${String(maxD.getMonth() + 1).padStart(2, '0')}`;

    let noDate = 0;
    let outsideHorizon = 0;
    const byMonth = {};
    inPeriod.forEach((l) => {
      const raw = String(l.eventDate || '');
      const key = /^\d{4}-\d{2}/.test(raw) ? raw.slice(0, 7) : '';
      if (!key) { noDate += 1; return; }
      if (key < minKey || key > maxKey) { outsideHorizon += 1; return; }
      byMonth[key] = (byMonth[key] || 0) + 1;
    });

    const bookedByMonth = {};
    (banquetAllRows || []).forEach((b) => {
      const key = String(b.function_date || '').slice(0, 7);
      if (key) bookedByMonth[key] = (bookedByMonth[key] || 0) + 1;
    });

    const monthLabel = (key) => {
      const [y, m] = key.split('-').map(Number);
      return new Date(y, m - 1, 1).toLocaleString(language === 'ur' ? 'ur-PK' : 'en-GB', { month: 'short', year: '2-digit' });
    };

    const rows = Object.keys(byMonth).sort().map((key) => ({
      month: monthLabel(key),
      inquiries: byMonth[key],
      booked: bookedByMonth[key] || 0,
    }));

    return { rows, noDate, outsideHorizon, total: inPeriod.length };
  }, [dashboardLeads, periodFrom, periodTo, banquetAllRows, language]);

  // Open leads carrying a quote — the records behind the Pipeline Value figure.
  const pipelineLeads = (metrics.drilldownLeads || [])
    .filter((l) => !isWonStage(l.stage) && l.stage !== 'Lost' && safeAmount(l.amount) > 0)
    .sort((a, b) => safeAmount(b.amount) - safeAmount(a.amount));

  // Helper: normalize source string same way metrics does
  const normalizeSource = (src) => {
    if (!src) return 'Unknown';
    const trimmed = src.trim();
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
  };

  const pct = (n, d) => (d ? ((n / d) * 100).toFixed(1).replace(/\.0$/, '') + '%' : '—');

  // ── Marketing Performance (§1–3 of the marketing spec) ──────────────────
  // Cohort semantics throughout: leads CREATED in the window; site visits and
  // bookings are counted on those same leads wherever they stand today. Visits
  // accept either signal (flag or current stage), matching the Tentative card,
  // so the dashboard never shows two different answers to the same question.
  const marketing = useMemo(() => {
    const visited = (l) => l.siteVisited === true || l.stage === 'Site Visited';
    const inPeriod = (dashboardLeads || []).filter((l) => {
      const created = String(l.inquiryDate || l.createdAt || '').slice(0, 10);
      return created && created >= periodFrom && created <= periodTo;
    });

    // §1 — per-source table for the selected period
    const bySource = {};
    inPeriod.forEach((l) => {
      const name = l.source || 'Unknown';
      const b = (bySource[name] = bySource[name] || { name, leads: 0, visits: 0, booked: 0 });
      b.leads += 1;
      if (visited(l)) b.visits += 1;
      if (isWonStage(l.stage)) b.booked += 1;
    });
    const sourceRows = Object.values(bySource).sort((a, b) => b.leads - a.leads);

    // §2 — Meta leads grouped by ad set (names only exist since 29 Jun 2026)
    const byAdset = {};
    inPeriod
      .filter((l) => normalizeSource(l.source) === normalizeSource('Meta Lead Gen'))
      .forEach((l) => {
        const key = l.adsetName || l.meta?.adset_name;
        if (!key) return;
        const b = (byAdset[key] = byAdset[key] || { name: key, leads: 0, visits: 0, booked: 0 });
        b.leads += 1;
        if (visited(l)) b.visits += 1;
        if (isWonStage(l.stage)) b.booked += 1;
      });
    const adsetRows = Object.values(byAdset).sort((a, b) => b.leads - a.leads);

    // §3 — fixed last-12-months trend for one selectable source. Deliberately
    // independent of the period filter: its job is making a bad month visible
    // the week it happens (Feb and Jun 2026 produced zero bookings and nobody
    // knew), which a single-period view can never show.
    const now = new Date();
    const trendRows = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      trendRows.push({
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
        label: d.toLocaleString(language === 'ur' ? 'ur-PK' : 'en-GB', { month: 'short', year: '2-digit' }),
        leads: 0, visits: 0, booked: 0
      });
    }
    const bucketByKey = Object.fromEntries(trendRows.map((b) => [b.key, b]));
    (dashboardLeads || []).forEach((l) => {
      if (marketingSource !== 'All' && normalizeSource(l.source) !== normalizeSource(marketingSource)) return;
      const b = bucketByKey[String(l.inquiryDate || l.createdAt || '').slice(0, 7)];
      if (!b) return;
      b.leads += 1;
      if (visited(l)) b.visits += 1;
      if (isWonStage(l.stage)) b.booked += 1;
    });

    // Selector options come from the whole dataset, not just the period —
    // you should be able to inspect a source's year even in a quiet week.
    const allSourceNames = [...new Set((dashboardLeads || []).map((l) => l.source).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b));

    return { sourceRows, adsetRows, trendRows, allSourceNames, periodTotal: inPeriod.length };
  }, [dashboardLeads, periodFrom, periodTo, marketingSource, language]);

  // §4 — media spend mapped onto the source table's period. A monthly ledger
  // can't honestly answer a one-week window, so costs render only for
  // month-or-longer periods, summing the calendar months in range.
  const spendCosts = useMemo(() => {
    const months = adSpend?.months || {};
    const from = new Date(periodFrom + 'T00:00:00');
    const to = new Date(periodTo + 'T00:00:00');
    if ((to - from) / 86400000 < 25) return { active: false, bySource: {}, adsetBySet: {}, adsetLeads: {}, adsetConvos: {}, fee: 0, monthCount: 0 };
    const keys = [];
    const cur = new Date(from.getFullYear(), from.getMonth(), 1);
    while (cur <= to) {
      keys.push(`${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}`);
      cur.setMonth(cur.getMonth() + 1);
    }
    const bySource = {};
    keys.forEach((m) => {
      Object.entries(months[m] || {}).forEach(([src, amt]) => {
        const v = Number(amt) || 0;
        if (v) bySource[src] = (bySource[src] || 0) + v;
      });
    });
    // Per-ad-set numbers over the same period months, all from Meta's ledger:
    // spend, Meta-counted leads (complete history — no attribution-era gap),
    // and messaging conversations for Click-to-WhatsApp sets. Spend and
    // results share one window, so Rs/Lead divides like with like.
    const sumMap = (root, out) => {
      keys.forEach((m) => {
        Object.entries(root?.[m] || {}).forEach(([name, amt]) => {
          const v = Number(amt) || 0;
          if (v) out[name] = (out[name] || 0) + v;
        });
      });
      return out;
    };
    const adsetBySet = sumMap(adSpend?.adsetMonths, {});
    const adsetLeads = sumMap(adSpend?.adsetLeadsMonths, {});
    const adsetConvos = sumMap(adSpend?.adsetConvoMonths, {});
    return {
      active: Object.keys(bySource).length > 0,
      bySource,
      adsetBySet,
      adsetLeads,
      adsetConvos,
      fee: (Number(adSpend?.agencyFeeMonthly) || 0) * keys.length,
      monthCount: keys.length
    };
  }, [adSpend, periodFrom, periodTo]);

  // Ad sets with spend but no CRM leads must still appear — the Rs 157k
  // Click-to-WhatsApp test produced messages, not lead-form leads, and a
  // spend-only row is a finding, not a join casualty.
  const adsetDisplayRows = useMemo(() => {
    const byName = {};
    marketing.adsetRows.forEach((r) => { byName[r.name] = { ...r }; });
    [
      ...Object.keys(spendCosts.adsetBySet || {}),
      ...Object.keys(spendCosts.adsetLeads || {}),
      ...Object.keys(spendCosts.adsetConvos || {})
    ].forEach((name) => {
      if (!byName[name]) byName[name] = { name, leads: 0, visits: 0, booked: 0 };
    });
    return Object.values(byName).sort((a, b) =>
      (spendCosts.adsetBySet[b.name] || 0) - (spendCosts.adsetBySet[a.name] || 0) || b.leads - a.leads);
  }, [marketing.adsetRows, spendCosts.adsetBySet, spendCosts.adsetLeads, spendCosts.adsetConvos]);

  // Ledger spend for one trend bucket: the selected source's figure, or the
  // month's total across sources when viewing All.
  const trendSpendFor = (monthKey) => {
    const m = adSpend?.months?.[monthKey];
    if (!m) return 0;
    if (marketingSource === 'All') return Object.values(m).reduce((s, v) => s + (Number(v) || 0), 0);
    const hit = Object.entries(m).find(([src]) => normalizeSource(src) === normalizeSource(marketingSource));
    return hit ? Number(hit[1]) || 0 : 0;
  };

  // Why we lose — the loss-reason breakdown for leads created in the period,
  // plus the number the raw conversion rate hides: a win rate measured only
  // against deals that were ever real. Leads lost as spam, wrong numbers or
  // idle browsers are excluded from the denominator, because no salesperson
  // could have won them.
  const lossAnalysis = useMemo(() => {
    const inPeriod = (dashboardLeads || []).filter((l) => {
      const created = String(l.inquiryDate || l.createdAt || '').slice(0, 10);
      return created && created >= periodFrom && created <= periodTo;
    });
    const lost = inPeriod.filter((l) => l.stage === 'Lost');
    const won = inPeriod.filter((l) => isWonStage(l.stage));

    const counts = {};
    let unrecorded = 0;
    lost.forEach((l) => {
      const id = l.lostReason;
      if (!id || !LOSS_REASON_BY_ID[id]) { unrecorded += 1; return; }
      counts[id] = (counts[id] || 0) + 1;
    });

    const rows = LOSS_REASONS
      .map((r) => ({ ...r, count: counts[r.id] || 0 }))
      .filter((r) => r.count > 0)
      .sort((a, b) => b.count - a.count);

    const disqualified = DISQUALIFYING_REASONS.reduce((n, id) => n + (counts[id] || 0), 0);
    const realLosses = lost.length - disqualified - unrecorded;
    const closed = won.length + Math.max(0, realLosses);

    return {
      totalLost: lost.length,
      won: won.length,
      rows,
      unrecorded,
      disqualified,
      realLosses: Math.max(0, realLosses),
      rawRate: inPeriod.length ? (won.length / inPeriod.length) * 100 : 0,
      qualifiedRate: closed ? (won.length / closed) * 100 : 0,
      recorded: lost.length - unrecorded
    };
  }, [dashboardLeads, periodFrom, periodTo]);

  // §5 — same month across years, straight from the banquet book. A young
  // month's low average is immaturity, not decline: value lands as upsells do.
  const yoy = useMemo(() => {
    const rows = {};
    (banquetAllRows || []).forEach((b) => {
      const fd = String(b.function_date || '');
      if (fd.slice(5, 7) !== yoyMonth) return;
      const y = fd.slice(0, 4);
      if (!/^\d{4}$/.test(y)) return;
      const r = (rows[y] = rows[y] || { year: y, events: 0, total: 0 });
      r.events += 1;
      r.total += Number(b.financial_summary?.invoice_net_amount) || 0;
    });
    const now = new Date();
    const curKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    return Object.values(rows)
      .sort((a, b) => b.year.localeCompare(a.year))
      .map((r) => ({ ...r, avg: r.events ? r.total / r.events : 0, maturing: `${r.year}-${yoyMonth}` >= curKey }));
  }, [banquetAllRows, yoyMonth]);

  const monthNames = useMemo(
    () => Array.from({ length: 12 }, (_, i) =>
      new Date(2000, i, 1).toLocaleString(language === 'ur' ? 'ur-PK' : 'en-GB', { month: 'long' })),
    [language]
  );
  // Short names feed the YoY month-grid picker (3×4 tiles, calendar-style).
  const monthNamesShort = useMemo(
    () => Array.from({ length: 12 }, (_, i) =>
      new Date(2000, i, 1).toLocaleString(language === 'ur' ? 'ur-PK' : 'en-GB', { month: 'short' })),
    [language]
  );
  // ── CRM Summary computed data (date-filtered) ───────────────────────────
  const crmSummary = useMemo(() => {
    const today = new Date();
    const todayStr = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
    const isValidDateStr = (d) => d && d !== '-';

    const fromTs = summaryFrom ? new Date(summaryFrom + 'T00:00:00').getTime() : 0;
    const toTs = summaryTo ? new Date(summaryTo + 'T23:59:59').getTime() : Infinity;

    const isInPeriod = (dateStr) => {
      if (!dateStr) return false;
      const t = new Date(dateStr).getTime();
      return !isNaN(t) && t >= fromTs && t <= toTs;
    };

    const isCsvOrImported = (l) => {
      const src = (l.source || '').toLowerCase();
      const notes = (l.notes || l.bookingNotes || '').toLowerCase();
      return ['import', 'csv', 'legacy', 'old'].some(w => src.includes(w) || notes.includes(w));
    };

    const isCreatedInPeriod = (l) => !isCsvOrImported(l) && isInPeriod(l.inquiryDate || l.createdAt || l.created_at || l.date);
    const isLeadActive = (l) => !isWonStage(l.stage) && l.stage !== 'Lost';

    const categorizeCalls = (leads) => {
      const overdue = [], todayCalls = [], upcoming = [];
      leads.forEach(lead => {
        const hasNextCall = !!lead.nextCallDate;
        const hasSiteVisit = isValidDateStr(lead.siteVisitDate);
        const targetDate = hasNextCall ? lead.nextCallDate : (hasSiteVisit ? lead.siteVisitDate : null);
        if (!targetDate) {
          if (Date.now() - new Date(lead.createdAt || 0).getTime() > 24 * 60 * 60 * 1000) overdue.push(lead);
          else todayCalls.push(lead);
          return;
        }
        const d = targetDate.split('T')[0];
        if (d < todayStr) overdue.push(lead);
        else if (d === todayStr) todayCalls.push(lead);
        else upcoming.push(lead);
      });
      return { overdue, today: todayCalls, upcoming };
    };

    const globalCallsLeads = [];
    const globalVisitsLeads = [];
    const globalTotalLeads = [];
    const globalActiveLeads = [];
    const leadsByStage = {};
    STAGES.forEach(s => { leadsByStage[s] = []; });

    const salesMap = {};
    dashboardLeads.forEach(lead => {
      const name = lead.manager || 'Unassigned';
      if (!salesMap[name]) {
        salesMap[name] = {
          name, total: [], active: [], won: [], lost: [],
          newLeads: [], contacted: [], quoted: [], siteVisited: [],
          callLeads: [], siteVisitLeads: [], revenue: 0
        };
      }

      const createdInPeriod = isCreatedInPeriod(lead);
      const wonInPeriod = createdInPeriod && isWonStage(lead.stage);
      const lostInPeriod = createdInPeriod && lead.stage === 'Lost';

      if (createdInPeriod) {
        salesMap[name].total.push(lead);
        globalTotalLeads.push(lead);

        if (isLeadActive(lead)) {
          if (leadsByStage[lead.stage]) {
            leadsByStage[lead.stage].push(lead);
          }
          salesMap[name].active.push(lead);
          globalActiveLeads.push(lead);

          if (lead.stage === 'New') salesMap[name].newLeads.push(lead);
          else if (lead.stage === 'Contacted') salesMap[name].contacted.push(lead);
          else if (lead.stage === 'Quoted') salesMap[name].quoted.push(lead);
          else if (lead.stage === 'Site Visited') salesMap[name].siteVisited.push(lead);
        }
      }

      if (wonInPeriod) {
        salesMap[name].won.push(lead);
        salesMap[name].revenue += safeAmount(lead.amount || lead.finalAmount || lead.quotationAmount);
        if (leadsByStage['Won']) leadsByStage['Won'].push(lead);
      }

      if (lostInPeriod) {
        salesMap[name].lost.push(lead);
        if (leadsByStage['Lost']) leadsByStage['Lost'].push(lead);
      }

      if (isLeadActive(lead)) {
        const isCallLead = lead.inCallList === true || !!lead.nextCallDate || (isValidDateStr(lead.siteVisitDate) && lead.stage !== 'Site Visited' && !lead.siteVisited);
        if (isCallLead) {
          salesMap[name].callLeads.push(lead);
          globalCallsLeads.push(lead);
        }

        if (isValidDateStr(lead.siteVisitDate) && lead.stage !== 'Site Visited' && !lead.siteVisited) {
          salesMap[name].siteVisitLeads.push(lead);
          globalVisitsLeads.push(lead);
        }
      }
    });

    const globalCalls = categorizeCalls(globalCallsLeads);

    const salespeople = Object.values(salesMap)
      .filter(sp => sp.total.length > 0 || sp.won.length > 0 || sp.lost.length > 0 || sp.callLeads.length > 0 || sp.siteVisitLeads.length > 0)
      .map(sp => ({
        ...sp,
        convRate: sp.total.length > 0 ? Math.round((sp.won.length / sp.total.length) * 100) : 0,
        calls: categorizeCalls(sp.callLeads),
        visits: {
          all: sp.siteVisitLeads,
          overdue: sp.siteVisitLeads.filter(l => (l.siteVisitDate || '').split('T')[0] < todayStr),
          today: sp.siteVisitLeads.filter(l => (l.siteVisitDate || '').split('T')[0] === todayStr),
          upcoming: sp.siteVisitLeads.filter(l => (l.siteVisitDate || '').split('T')[0] > todayStr),
        },
      }))
      .sort((a, b) => b.total.length - a.total.length || b.revenue - a.revenue);

    return {
      totalLeads: globalTotalLeads.length,
      allLeads: globalTotalLeads,
      activeLeads: globalActiveLeads,
      leadsByStage,
      callLeads: globalCallsLeads,
      overdueCallLeads: globalCalls.overdue,
      todayCallLeads: globalCalls.today,
      upcomingCallLeads: globalCalls.upcoming,
      siteVisitLeads: globalVisitsLeads,
      overdueVisits: globalVisitsLeads.filter(l => (l.siteVisitDate || '').split('T')[0] < todayStr),
      todayVisits: globalVisitsLeads.filter(l => (l.siteVisitDate || '').split('T')[0] === todayStr),
      upcomingVisits: globalVisitsLeads.filter(l => (l.siteVisitDate || '').split('T')[0] > todayStr),
      salespeople,
    };
  }, [dashboardLeads, summaryFrom, summaryTo]);


  // ── Summary modal leads list ─────────────────────────────────────────────
  const getSummaryModalLeads = () => {
    if (!summaryModalType) return [];
    // Salesperson-scoped modal: 'sp:Name:type'
    if (summaryModalType.startsWith('sp:')) {
      const parts = summaryModalType.split(':');
      const spName = parts[1];
      const type = parts[2];
      const sp = crmSummary.salespeople.find(s => s.name === spName);
      if (!sp) return [];
      switch (type) {
        case 'total': return sp.total;
        case 'active': return sp.active;
        case 'new': return sp.newLeads;
        case 'contacted': return sp.contacted;
        case 'quoted': return sp.quoted;
        case 'siteVisited': return sp.siteVisited;
        case 'won': return sp.won;
        case 'lost': return sp.lost;
        case 'calls-all': return sp.callLeads;
        case 'calls-overdue': return sp.calls.overdue;
        case 'calls-today': return sp.calls.today;
        case 'calls-upcoming': return sp.calls.upcoming;
        case 'visits-all': return sp.visits.all;
        case 'visits-overdue': return sp.visits.overdue;
        case 'visits-today': return sp.visits.today;
        case 'visits-upcoming': return sp.visits.upcoming;
        default: return [];
      }
    }
    if (summaryModalType.startsWith('stage-')) {
      const stage = summaryModalType.replace('stage-', '');
      return crmSummary.leadsByStage[stage] || [];
    }
    switch (summaryModalType) {
      case 'leads-all': return crmSummary.allLeads;
      case 'leads-active': return crmSummary.activeLeads;
      case 'calls-all': return crmSummary.callLeads;
      case 'calls-overdue': return crmSummary.overdueCallLeads;
      case 'calls-today': return crmSummary.todayCallLeads;
      case 'calls-upcoming': return crmSummary.upcomingCallLeads;
      case 'visits-all': return crmSummary.siteVisitLeads;
      case 'visits-overdue': return crmSummary.overdueVisits;
      case 'visits-today': return crmSummary.todayVisits;
      case 'visits-upcoming': return crmSummary.upcomingVisits;
      default: return [];
    }
  };

  const getSummaryModalTitle = () => {
    if (!summaryModalType) return '';
    if (summaryModalType.startsWith('sp:')) {
      const parts = summaryModalType.split(':');
      const spName = parts[1];
      const type = parts[2];
      const leads = getSummaryModalLeads();
      const typeLabel = {
        'total': 'All Leads', 'active': 'Active', 'won': 'Won', 'lost': 'Lost',
        'calls-all': 'Calls', 'calls-overdue': 'Overdue Calls', 'calls-today': "Today's Calls", 'calls-upcoming': 'Upcoming Calls',
        'visits-all': 'Site Visits', 'visits-overdue': 'Overdue Visits', 'visits-today': "Today's Visits", 'visits-upcoming': 'Upcoming Visits',
      }[type] || type;
      return `${spName} — ${typeLabel} (${leads.length})`;
    }
    if (summaryModalType.startsWith('stage-')) {
      const stage = summaryModalType.replace('stage-', '');
      const count = (crmSummary.leadsByStage[stage] || []).length;
      return `Stage: ${stage} (${count} leads)`;
    }
    const labels = {
      'leads-all': `All Leads (${crmSummary.allLeads.length})`,
      'leads-active': `Active Leads (${crmSummary.activeLeads.length})`,
      'calls-all': `All Calls (${crmSummary.callLeads.length})`,
      'calls-overdue': `Overdue Calls (${crmSummary.overdueCallLeads.length})`,
      'calls-today': `Today's Calls (${crmSummary.todayCallLeads.length})`,
      'calls-upcoming': `Upcoming Calls (${crmSummary.upcomingCallLeads.length})`,
      'visits-all': `All Site Visits (${crmSummary.siteVisitLeads.length})`,
      'visits-overdue': `Overdue Site Visits (${crmSummary.overdueVisits.length})`,
      'visits-today': `Today's Site Visits (${crmSummary.todayVisits.length})`,
      'visits-upcoming': `Upcoming Site Visits (${crmSummary.upcomingVisits.length})`,
    };
    return labels[summaryModalType] || '';
  };

  // Get filtered leads for drilldown
  const getFilteredLeads = () => {
    switch (activeModal) {
      case 'leads':
        return metrics.drilldownLeads;
      case 'booked':
        // C-02: use the server-side bookedAt-filtered list, NOT a client-side stage filter on createdAt data
        return metrics.bookedDrilldownLeads;
      case 'conversion':
        return metrics.bookedDrilldownLeads;
      case 'stale':
        return metrics.staleLeads;
      case 'pipeline':
        return pipelineLeads;
      case 'tentative':
        return tentativeLeads;
      case 'ytd':
        return metrics.ytdDrilldownLeads;
      case 'source':
        return selectedSource
          // Normalize BOTH sides: selectedSource arrives verbatim from the server
          // ("Meta Lead Gen") while normalizeSource lowercases everything after
          // the first letter ("Meta lead gen") — one-sided normalization made
          // every multi-word source with mid-name capitals filter to an empty
          // list (Meta Lead Gen, Whatsapp Message, Mobile Number, Instagram
          // Message), while single-cap names (Walkin, Landline) worked.
          ? metrics.drilldownLeads.filter((l) => normalizeSource(l.source) === normalizeSource(selectedSource))
          : [];
      case 'funnel':
        return selectedStage
          ? metrics.drilldownLeads.filter((l) => {
            const cap = (s) => s ? s.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ') : 'New';
            const stageMapping = { 'New Lead': 'New', 'Proposal': 'Quoted', 'Booked': 'Won', 'Negotiation': 'Negotiation' };
            const normalized = stageMapping[cap(l.stage)] || cap(l.stage);
            return normalized === selectedStage;
          })
          : [];
      default:
        return [];
    }
  };

  const getModalTitle = () => {
    switch (activeModal) {
      case 'leads': return `${t('leadsThisMonth')} (${metrics.periodLeads})`;
      case 'booked': return `Booked This Period (${metrics.bookedPeriodLeads})`;
      case 'conversion': return `${t('bookedLeads')} (${metrics.conversionRate}% ${t('conversionRate').toLowerCase()})`;
      case 'stale': return `${t('staleLeads')} (${metrics.staleLeads.length})`;
      case 'pipeline': return `${t('pipelineValue')} — ${formatCurrency(analyticsData?.pipelinePotential || 0)} (${pipelineLeads.length})`;
      case 'tentative': return `${t('tentativeBookings')} (${tentativeLeads.length})`;
      case 'ytd': return `${t('revenueYtd')} - ${formatCurrency(metrics.ytdRevenue)}`;
      case 'source': return `${t('source')}: ${selectedSource}`;
      case 'funnel': return `${t('stage')}: ${selectedStage}`;
      default: return '';
    }
  };

  // ── Shared lead row renderer for all modals ──────────────────────────────
  // showBookedOn: only the analytics drill-down lists carry bookedAt/inquiryDate
  // from the server — SummaryModal leads don't, so it stays off there.
  const LeadTableRow = ({ lead, index = 0, isBookedList = false, showBookedOn = false }) => (
    <tr
      key={lead.id}
      className="hover:bg-blue-50/50 cursor-pointer transition-colors"
      onClick={() => navigate(`?leadId=${lead.id}`)}
    >
      <td className="px-4 py-3 max-w-[200px]">
        <div className="font-semibold text-gray-900 truncate">{lead.clientName}</div>
        <div className="text-xs text-gray-400 truncate">{lead.phone}</div>
      </td>
      {showBookedOn && (
        <td className="px-4 py-3 text-xs whitespace-nowrap">
          {lead.bookedAt ? (
            <span className="text-gray-700 font-medium">
              {formatDate(lead.bookedAt, { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          ) : (
            <span
              className="text-amber-600 font-medium"
              title={t('bookedOnAssumedNote') || 'No booking timestamp on record — this lead is counted in the period by its inquiry date instead.'}
            >
              {lead.inquiryDate ? formatDate(lead.inquiryDate, { day: 'numeric', month: 'short', year: 'numeric' }) : '—'} *
            </span>
          )}
        </td>
      )}
      {!isBookedList && (
        <>
          <td className="px-4 py-3 whitespace-nowrap">
            <span className={`px-2 py-1 rounded-lg text-xs font-semibold whitespace-nowrap ${STAGE_COLORS[lead.stage]?.bg || 'bg-gray-100'} ${STAGE_COLORS[lead.stage]?.text || 'text-gray-600'}`}>
              {lead.stage}
            </span>
          </td>
          <td className="px-4 py-3 hidden sm:table-cell whitespace-nowrap">
            <span className="text-xs text-gray-500">{lead.manager || '—'}</span>
          </td>
          <td className="px-4 py-3 hidden sm:table-cell whitespace-nowrap">
            {lead.source ? (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">{lead.source}</span>
            ) : <span className="text-gray-300">—</span>}
          </td>
          <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
            {formatDate(lead.createdAt, { day: 'numeric', month: 'short', year: 'numeric' })}
          </td>
        </>
      )}

      <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
        {lead.eventDate ? formatDate(lead.eventDate, { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
      </td>

      {isBookedList ? (
        <>
          <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
            {lead.venue || '—'}
          </td>
          <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
            {lead.shift || '—'}
          </td>
          <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
            {lead.manager || '—'}
          </td>
        </>
      ) : (
        <>
          <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
            {lead.clientBudget ? formatCurrency(lead.clientBudget) : '—'}
          </td>
          <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap font-medium">
            {lead.amount || lead.quotationAmount ? formatCurrency(lead.amount || lead.quotationAmount) : '—'}
          </td>
        </>
      )}

      <td className="px-4 py-3 text-center">
        <a
          href={getWhatsappLink(lead.phone)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={e => e.stopPropagation()}
          className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg inline-block"
        >
          <MessageCircle className="w-4 h-4" />
        </a>
      </td>
    </tr>
  );

  // ── Summary Modal (CRM card drill-down) ──────────────────────────────────
  const SummaryModal = () => {
    if (!summaryModalType) return null;
    const leads = getSummaryModalLeads();
    const visible = leads.slice(0, visibleCount);
    const hasMore = visibleCount < leads.length;
    const isBookedList = summaryModalType.toLowerCase().includes('won');

    return (
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onMouseDown={e => { if (e.target === e.currentTarget) { setSummaryModalType(null); setVisibleCount(50); } }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ type: "spring", duration: 0.5, bounce: 0.2 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-[95vw] max-h-[80vh] overflow-hidden flex flex-col"
        >
          <div className="p-4 border-b bg-gray-50 flex justify-between items-center flex-shrink-0">
            <h3 className="font-bold text-gray-900 text-base">{getSummaryModalTitle()}</h3>
            <button
              onClick={() => { setSummaryModalType(null); setVisibleCount(50); }}
              className="p-2 hover:bg-gray-200 rounded-lg"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <div className="overflow-y-auto flex-1">
            {leads.length === 0 ? (
              <div className="p-12 text-center text-gray-400">
                <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="font-medium">No leads found</p>
              </div>
            ) : (
              <>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr className="text-left text-gray-500 whitespace-nowrap">
                      <th className="px-4 py-3">{t('client')}</th>
                      {!isBookedList && (
                        <>
                          <th className="px-4 py-3">{t('stage')}</th>
                          <th className="px-4 py-3 hidden sm:table-cell">{t('assigned')}</th>
                          <th className="px-4 py-3 hidden sm:table-cell">{t('source')}</th>
                          <th className="px-4 py-3">{t('createdDate')}</th>
                        </>
                      )}
                      <th className="px-4 py-3">{t('eventDate')}</th>
                      {isBookedList ? (
                        <>
                          <th className="px-4 py-3">Venue</th>
                          <th className="px-4 py-3">Shift</th>
                          <th className="px-4 py-3">{t('assigned')}</th>
                        </>
                      ) : (
                        <>
                          <th className="px-4 py-3">{t('clientBudget')}</th>
                          <th className="px-4 py-3">{t('quotation')}</th>
                        </>
                      )}
                      <th className="px-4 py-3 text-center">{t('wa')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {visible.map((lead, i) => <LeadTableRow key={lead.id} lead={lead} index={i} isBookedList={isBookedList} />)}
                  </tbody>
                </table>
                {hasMore && (
                  <div className="flex justify-center py-5">
                    <button
                      onClick={() => setVisibleCount(c => c + 50)}
                      className="px-4 py-1.5 bg-gray-100 text-gray-600 hover:bg-gray-200 text-sm font-medium rounded-lg transition-colors"
                    >
                      {t('loadMoreRemaining').replace('{remaining}', leads.length - visibleCount)}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="p-3 border-t bg-gray-50 text-xs text-gray-500 flex-shrink-0 flex items-center justify-between">
            <span>{t('showingXofYleads').replace('{x}', visible.length).replace('{y}', leads.length)}</span>
          </div>
        </motion.div>
      </div>
    );
  };

  // Drilldown Modal Component
  const DrilldownModal = () => {
    if (!activeModal) return null;
    const filteredLeads = getFilteredLeads();
    const visibleLeads = filteredLeads.slice(0, visibleCount);
    const hasMore = visibleCount < filteredLeads.length;
    const isBookedList = activeModal === 'booked' || activeModal === 'conversion';

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) { setActiveModal(null); setSelectedSource(null); setSelectedStage(null); setVisibleCount(50); } }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ type: "spring", duration: 0.5, bounce: 0.2 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-[95vw] max-h-[80vh] overflow-hidden flex flex-col"
        >
          <div className="p-4 border-b bg-gray-50 flex justify-between items-center flex-shrink-0">
            <h3 className="font-bold text-gray-900">{getModalTitle()}</h3>
            <button
              onClick={() => { setActiveModal(null); setSelectedSource(null); setSelectedStage(null); setVisibleCount(50); }}
              className="p-2 hover:bg-gray-200 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="overflow-y-auto flex-1">
            {filteredLeads.length === 0 ? (
              <div className="p-8 text-center text-gray-400">{t('noBookingsFound').replace('bookings', 'leads')}</div>
            ) : (
              <>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr className="text-left text-gray-500 whitespace-nowrap">
                      <th className="px-4 py-3">{t('client')}</th>
                      {isBookedList && (
                        <th className="px-4 py-3">{t('bookedOn') || 'Booked On'}</th>
                      )}
                      {!isBookedList && (
                        <>
                          <th className="px-4 py-3">{t('stage')}</th>
                          <th className="px-4 py-3 hidden sm:table-cell">{t('assigned')}</th>
                          <th className="px-4 py-3 hidden sm:table-cell">{t('source')}</th>
                          <th className="px-4 py-3">{t('createdDate')}</th>
                        </>
                      )}
                      <th className="px-4 py-3">{t('eventDate')}</th>
                      {isBookedList ? (
                        <>
                          <th className="px-4 py-3">Venue</th>
                          <th className="px-4 py-3">Shift</th>
                          <th className="px-4 py-3">{t('assigned')}</th>
                        </>
                      ) : (
                        <>
                          <th className="px-4 py-3">{t('clientBudget')}</th>
                          <th className="px-4 py-3">{t('quotation')}</th>
                        </>
                      )}
                      <th className="px-4 py-3 text-center">{t('wa')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {visibleLeads.map((lead, i) => (
                      <LeadTableRow key={lead.id} lead={lead} index={i} isBookedList={isBookedList} showBookedOn={isBookedList} />
                    ))}
                  </tbody>
                </table>
                {hasMore && (
                  <div className="flex justify-center py-5">
                    <button
                      onClick={() => setVisibleCount((c) => c + 50)}
                      className="px-6 py-2 border border-blue-500 text-blue-600 hover:bg-blue-50 text-sm font-medium rounded-lg transition-colors"
                    >
                      {t('loadMoreRemaining').replace('{remaining}', filteredLeads.length - visibleCount)}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="p-3 md:p-4 border-t bg-gray-50 text-xs text-gray-500 flex-shrink-0 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <div>
              {t('showingXofYleads').replace('{x}', visibleLeads.length).replace('{y}', filteredLeads.length)}
            </div>
            {isBookedList && (
              <div className="flex flex-col items-start sm:items-end gap-1.5">
                {filteredLeads.some(l => !l.bookedAt) && (
                  <div className="inline-flex items-center px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-[11px] font-medium text-amber-700">
                    <span>{t('bookedOnAssumedNote') || '* No booking timestamp on record — these leads are counted in the period by their inquiry date, so the actual booking may belong to a different period.'}</span>
                  </div>
                )}
                <div className="inline-flex items-center px-2.5 py-1 rounded-full bg-gradient-to-r from-blue-50 to-indigo-50 border border-indigo-100 text-[11px] font-medium text-indigo-700">
                  <span>{t('d05CaveatText') || 'Per-lead money only matches where the CRM booking reference exists. Bookings made straight into the banquet system won\'t credit a source or salesperson.'}</span>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    );
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Drilldown Modal */}
      {DrilldownModal()}
      {/* CRM Summary Modal */}
      {SummaryModal()}
      <BookingDetailModal
        event={selectedBookingForModal}
        onClose={() => setSelectedBookingForModal(null)}
        onViewLead={(event) => { setSelectedBookingForModal(null); setTimeout(() => navigate(`?leadId=${event.crmId || event.lead?.id}`), 200); }}
        t={t}
        currentUser={{}}
      />

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
            {t('ownerDashboard')}
          </h1>
          <p className="text-gray-500 mt-1">
            {t('welcomeBack')}, <span className="font-semibold text-gray-900">{/* currentUser?.name || */ 'Admin'}</span>
          </p>
        </div>

        {/* Quick Actions — rolling windows (see dateRange comment above) */}
        <div className="flex flex-col items-end gap-2">
          <div className="flex bg-white rounded-xl shadow-sm border p-1 overflow-x-auto whitespace-nowrap hide-scrollbar">
            {[
              ['30d', t('last30Days') || '30 Days'],
              ['90d', t('last90Days') || '90 Days'],
              ['ytd', t('ytdLabel') || 'YTD'],
              ['12m', t('last12Months') || '12 Months'],
              ['all', t('allTime') || 'All'],
              ['custom', t('customRange') || 'Custom'],
            ].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setDateRange(key)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${dateRange === key ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'
                  }`}
              >
                {label}
              </button>
            ))}
          </div>
          {dateRange === 'custom' && (
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-1.5 shadow-sm">
              <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
              <div className="w-32">
                <DatePicker
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="border-0 shadow-none bg-transparent !p-0 min-h-0 !h-auto text-xs sm:text-sm font-medium"
                  fullWidth
                />
              </div>
              <span className="text-gray-300 text-xs shrink-0">→</span>
              <div className="w-32">
                <DatePicker
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="border-0 shadow-none bg-transparent !p-0 min-h-0 !h-auto text-xs sm:text-sm font-medium"
                  fullWidth
                />
              </div>
            </div>
          )}
        </div>
      </div>



      {/* Stats Grid */}
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          {t('quickStats')}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4">

          {/* Pipeline Value (CRM) */}
          <div
            onClick={() => setActiveModal('pipeline')}
            className="bg-white p-4 rounded-2xl border shadow-sm transition-all group relative overflow-hidden cursor-pointer hover:shadow-md hover:border-indigo-200"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-indigo-100 text-indigo-600 rounded-xl group-hover:scale-110 transition-transform">
                <TrendingUp className="w-5 h-5" />
              </div>
              <span className="text-[10px] text-blue-500 font-medium bg-blue-50 px-2 py-0.5 rounded-full">{t('viewList')}</span>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate" title="Pipeline Value (CRM)">{t('pipelineValue') || 'Pipeline Value (CRM)'}</p>
              <h3 className="text-lg font-bold text-gray-900 mt-1 truncate">{formatCurrency(analyticsData?.pipelinePotential || 0)}</h3>
            </div>
          </div>

          {/* Money group: one fence around the three banquet-money cards, with
              the axis toggle ON the fence — control proximity tells the user
              its blast radius. Booking axis = BMS order_date (sales made in
              period, today's behaviour); event axis = function_date (what's on
              the books FOR the period's events, whenever it was sold).
              Pipeline sits outside the fence: it's open quotes, not booked
              money, and no axis applies to it. */}
          <div className="col-span-2 md:col-span-3 rounded-2xl border border-gray-200 bg-slate-50/60 p-3">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2 px-1">
              <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{t('revenueGroup') || 'Revenue (Banquet)'}</span>
              <div className="flex bg-gray-100 p-0.5 rounded-lg">
                <button
                  onClick={() => setRevenueAxis('booking')}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-all ${revenueAxis === 'booking' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  {t('byBookingDate') || 'By booking date'}
                </button>
                <button
                  onClick={() => setRevenueAxis('event')}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition-all ${revenueAxis === 'event' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  {t('byEventDate') || 'By event date'}
                </button>
              </div>
            </div>
            {revenueAxis === 'event' && (
              <p className="text-[11px] text-gray-400 mb-2 px-1 leading-snug">
                {t('eventAxisNote') || 'Events in this period, whenever booked — straight from the banquet book, including bookings that never touched the CRM.'}
                {periodTo >= fmtDay(new Date()) && (
                  <span className="text-amber-600 font-semibold"> {t('stillMaturingNote') || 'Still maturing — more bookings will land for these dates.'}</span>
                )}
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">

              {/* Booked Revenue (Banquet) */}
              <div
                onClick={() => revenueAxis === 'booking' && openInFinance('all')}
                className={`bg-white p-4 rounded-2xl border shadow-sm transition-all group relative overflow-hidden ${revenueAxis === 'booking' ? 'cursor-pointer hover:shadow-md hover:border-green-200' : ''}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="p-2 bg-green-100 text-green-600 rounded-xl group-hover:scale-110 transition-transform">
                    <DollarSign className="w-5 h-5" />
                  </div>
                  {revenueAxis === 'booking' && <span className="text-[10px] text-blue-500 font-medium bg-blue-50 px-2 py-0.5 rounded-full">{t('viewList')}</span>}
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium truncate">
                    {revenueAxis === 'event' ? (t('revenueForEvents') || 'For events in period') : (t('bookedRevenue') || 'Booked (Banquet)')}
                  </p>
                  <h3 className="text-lg font-bold text-gray-900 mt-1 truncate">{formatCurrency(fin.booked)}</h3>
                  {/* The count belongs to the same axis as the money: in event
                      mode this is the number of functions ON the calendar in
                      the window — "Booked This Period" outside the fence stays
                      booking-date and correctly reads 0 for future windows. */}
                  {revenueAxis === 'event' && (
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      {eventFinancials.count} {t('events') || 'events'}
                    </p>
                  )}
                </div>
              </div>

              {/* Collected (Banquet) */}
              <div
                onClick={() => revenueAxis === 'booking' && openInFinance('collected')}
                className={`bg-white p-4 rounded-2xl border shadow-sm transition-all group relative overflow-hidden ${revenueAxis === 'booking' ? 'cursor-pointer hover:shadow-md hover:border-teal-200' : ''}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="p-2 bg-teal-100 text-teal-600 rounded-xl group-hover:scale-110 transition-transform">
                    <Banknote className="w-5 h-5" />
                  </div>
                  {revenueAxis === 'booking' && <span className="text-[10px] text-blue-500 font-medium bg-blue-50 px-2 py-0.5 rounded-full">{t('viewList')}</span>}
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium truncate">
                    {revenueAxis === 'event' ? (t('collectedForEvents') || 'Collected for these events') : (t('collectedRevenue') || 'Collected (Banquet)')}
                  </p>
                  <h3 className="text-lg font-bold text-gray-900 mt-1 truncate">{formatCurrency(fin.collected)}</h3>
                </div>
              </div>

              {/* Outstanding (Banquet) */}
              <div
                onClick={() => revenueAxis === 'booking' && openInFinance('outstanding')}
                className={`bg-white p-4 rounded-2xl border shadow-sm transition-all group relative overflow-hidden ${revenueAxis === 'booking' ? 'cursor-pointer hover:shadow-md hover:border-orange-200' : ''}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="p-2 bg-orange-100 text-orange-600 rounded-xl group-hover:scale-110 transition-transform">
                    <AlertTriangle className="w-5 h-5" />
                  </div>
                  {revenueAxis === 'booking' && <span className="text-[10px] text-blue-500 font-medium bg-blue-50 px-2 py-0.5 rounded-full">{t('viewList')}</span>}
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium truncate">
                    {revenueAxis === 'event' ? (t('outstandingForEvents') || 'Outstanding for these events') : (t('outstandingBalance') || 'Outstanding (Banquet)')}
                  </p>
                  <h3 className="text-lg font-bold text-gray-900 mt-1 truncate">{formatCurrency(fin.outstanding)}</h3>
                </div>
              </div>
            </div>
          </div>

          {/* Revenue Card Removed (C-01) */}

          {/* Booked This Period — the HEADLINE count, read straight from the
              banquet system (C-02/D-01: booking figures come from the BMS).
              Complete: includes walk-ins and direct BMS bookings that never
              passed through the CRM, which lead-side counting can never cover.
              View List = the same bookings in Finance, period applied. The
              CRM-lead subset (attribution) lives in the Sales Team Summary. */}
          <div
            onClick={() => openInFinance('all')}
            className="bg-white p-4 rounded-2xl border shadow-sm cursor-pointer hover:shadow-md hover:border-blue-200 transition-all"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-blue-100 text-blue-600 rounded-xl">
                <CheckCircle className="w-5 h-5" />
              </div>
              <span className="text-[10px] text-blue-500 font-medium bg-blue-50 px-2 py-0.5 rounded-full">View List</span>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate" title="Booked This Period (Banquet)">Booked This Period</p>
              <h3 className="text-lg font-bold text-gray-900 mt-1">{banquetFinancials.count}</h3>
              {winsWithoutBookingDate > 0 && (
                <p className="text-[10px] text-amber-600 mt-1 leading-tight">
                  {winsWithoutBookingDate} {t('winsWithoutBookingDate')}
                </p>
              )}
            </div>
          </div>

          {/* Tentative Bookings — leads currently at Site Visited */}
          <div
            onClick={() => setActiveModal('tentative')}
            className="bg-white p-4 rounded-2xl border shadow-sm cursor-pointer hover:shadow-md hover:border-purple-200 transition-all"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-purple-100 text-purple-600 rounded-xl">
                <Calendar className="w-5 h-5" />
              </div>
              <span className="text-[10px] text-blue-500 font-medium bg-blue-50 px-2 py-0.5 rounded-full">{t('viewList')}</span>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate">{t('tentativeBookings')}</p>
              <h3 className="text-lg font-bold text-gray-900 mt-1">{tentativeLeads.length}</h3>
            </div>
          </div>

          {/* Active Leads */}
          <div
            onClick={() => setActiveModal('leads')}
            className="bg-white p-4 rounded-2xl border shadow-sm cursor-pointer hover:shadow-md transition-all"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-amber-100 text-amber-600 rounded-xl">
                <Users className="w-5 h-5" />
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate">{t('activeLeads')}</p>
              <h3 className="text-lg font-bold text-gray-900 mt-1">{metrics.activePeriodLeads}</h3>
            </div>
          </div>

          {/* Conversion Rate */}
          <div className="bg-white p-4 rounded-2xl border shadow-sm">
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-purple-100 text-purple-600 rounded-xl">
                <Target className="w-5 h-5" />
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate">{t('conversionRate')}</p>
              <h3 className="text-lg font-bold text-gray-900 mt-1">{metrics.conversionRate}%</h3>
            </div>
          </div>

          {/* YTD Revenue Card Removed (C-01) */}

          {/* Avg Response Time */}
          <div className="bg-white p-4 rounded-2xl border shadow-sm">
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 bg-cyan-100 text-cyan-600 rounded-xl">
                <Timer className="w-5 h-5" />
              </div>
              {metrics.avgResponseTime !== null && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${metrics.avgResponseTime <= 30 ? 'bg-green-100 text-green-700' : metrics.avgResponseTime <= 120 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                  }`}>
                  {metrics.avgResponseTime <= 30 ? t('excellent') : metrics.avgResponseTime <= 120 ? t('good') : t('needsImprovement')}
                </span>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium truncate">{t('avgResponseTime')}</p>
              <h3 className="text-lg font-bold text-gray-900 mt-1">
                {metrics.avgResponseTime !== null ? (
                  metrics.avgResponseTime < 60
                    ? `${metrics.avgResponseTime}m`
                    : `${Math.round(metrics.avgResponseTime / 60)}h`
                ) : (
                  '—'
                )}
              </h3>
            </div>
          </div>
        </div>
      </div>

      {/* D-05 Note Banner */}
      <div className="bg-gradient-to-r from-blue-50/80 via-indigo-50/80 to-purple-50/80 border border-indigo-100/80 rounded-xl px-3.5 py-2.5">
        <p className="text-xs text-indigo-950/80 font-medium leading-relaxed">
          <span className="font-bold text-indigo-700">{t('note') || 'Note'}:</span> {t('d05CaveatText') || 'Per-lead money only matches where the CRM booking reference exists. Bookings made straight into the banquet system won\'t credit a source or salesperson.'}
        </p>
      </div>

      {/* Charts Row 1 - Stack on mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Revenue Trend */}
        <div className="lg:col-span-2 bg-white p-4 md:p-5 rounded-xl border shadow-sm">
          <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2 text-sm md:text-base">
            <BarChart3 className="w-4 h-4" /> {t('leadsBookingsTrend')}
          </h3>
          <div className="h-48 md:h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={metrics.trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 10 }} width={30} />
                <Tooltip formatter={(value, name) => [value.toLocaleString(), name]} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="leads"
                  stroke="#3B82F6"
                  fill="#3B82F6"
                  fillOpacity={0.1}
                  name={t('leads') || 'Leads'}
                />
                {/* Bookings taken, plotted against the same axis. Deliberately a
                    COUNT, not money: the only revenue figure available here is
                    derived from CRM quote fields, which D-01 rules out. A true
                    revenue line needs the banquet system's figures bucketed by
                    date server-side — see follow-up note in the PR. */}
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="booked"
                  stroke="#10B981"
                  fill="#10B981"
                  fillOpacity={0.15}
                  name={t('bookings') || 'Bookings'}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Lead Sources */}
        <div className="bg-white p-4 md:p-5 rounded-xl border shadow-sm">
          <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2 text-sm md:text-base">
            <PieChart className="w-4 h-4" /> {t('trafficSources')}
          </h3>
          <div className="h-36 md:h-48">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsPie>
                <Pie
                  data={sourceData}
                  cx="50%"
                  cy="50%"
                  innerRadius={35}
                  outerRadius={55}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {sourceData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [`${value.toLocaleString()} ${t('leads').toLowerCase()}`, name]} />
              </RechartsPie>
            </ResponsiveContainer>
          </div>
          {/* Legend below chart - clickable */}
          <div className="mt-2 grid grid-cols-2 gap-x-2 gap-y-1 text-[10px] md:text-xs">
            {sourceData.map((entry, index) => (
              <div
                key={entry.name}
                className="flex items-center gap-1 truncate cursor-pointer hover:bg-gray-100 p-1 rounded"
                onClick={() => { setSelectedSource(entry.name); setActiveModal('source'); }}
              >
                <span
                  className="w-2 h-2 md:w-3 md:h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
                <span className="truncate text-gray-600">{entry.name === 'Unknown' ? t('unknownSource') : entry.name}</span>
                <span className="font-bold text-gray-800 ml-auto">
                  {periodSourceTotal > 0 ? ((entry.value / periodSourceTotal) * 100).toFixed(0) : 0}%
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Demand by event month (§6): demand vs confirmed supply ── */}
      <div className="bg-white p-4 md:p-5 rounded-xl border shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 mb-4">
          <h3 className="font-bold text-gray-800 flex items-center gap-2 text-sm md:text-base">
            <Calendar className="w-4 h-4" /> {t('demandByEventMonth') || 'Demand by Event Month'}
          </h3>
          <p className="text-[11px] text-gray-400">
            {t('demandSubtitle') || 'Inquiries received in the selected period, by the event month they asked about'}
          </p>
        </div>
        {demand.rows.length === 0 ? (
          <div className="h-40 flex items-center justify-center text-sm text-gray-400">
            {t('noDemandData') || 'No dated inquiries in this period'}
          </div>
        ) : (
          <div className="h-56 md:h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={demand.rows} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} width={30} allowDecimals={false} />
                <Tooltip formatter={(value, name) => [value.toLocaleString(), name]} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar dataKey="inquiries" name={t('inquiriesLabel') || 'Inquiries'} fill="#6366F1" radius={[3, 3, 0, 0]} />
                <Bar dataKey="booked" name={t('alreadyBookedBms') || 'Already booked (BMS)'} fill="#10B981" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        <p className="mt-2 text-[11px] text-gray-400">
          {demand.noDate > 0 && `${demand.noDate} ${t('noEventDateExcluded') || 'inquiries without a usable event date excluded'}`}
          {demand.noDate > 0 && demand.outsideHorizon > 0 && ' · '}
          {demand.outsideHorizon > 0 && `${demand.outsideHorizon} ${t('outsideHorizonExcluded') || 'with event dates outside the next 18 months excluded'}`}
          {(demand.noDate > 0 || demand.outsideHorizon > 0) && ' · '}
          {t('bookedFromBms') || 'Booked counts come from the banquet system'}
        </p>
      </div>

      {/* ── Marketing Performance (§1–3): the numbers that decide the ad spend ── */}
      <div className="bg-white p-4 md:p-5 rounded-xl border shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 mb-4">
          <h3 className="font-bold text-gray-800 flex items-center gap-2 text-sm md:text-base">
            <TrendingUp className="w-4 h-4" /> {t('marketingPerformance') || 'Marketing Performance'}
          </h3>
          <p className="text-[11px] text-gray-400">
            {t('marketingCohortNote') || 'Leads created in the selected period; visits and bookings counted on those same leads'}
          </p>
        </div>

        {/* §1 Source performance — click a row for that source's leads */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-gray-200 text-left">
                <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase">{t('source')}</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase text-right">{t('leads')}</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase text-right">{t('siteVisits') || 'Site Visits'}</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase text-right">{t('visitRate') || 'Visit rate'}</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase text-right">{t('booked') || 'Booked'}</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase text-right">{t('conversionRate') || 'Conversion'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {marketing.sourceRows.map((r) => (
                <tr
                  key={r.name}
                  className="hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => { setSelectedSource(r.name); setActiveModal('source'); }}
                >
                  <td className="px-3 py-2 font-medium text-gray-800">{r.name}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.leads.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.visits}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-500">{pct(r.visits, r.leads)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.booked}</td>
                  <td className={`px-3 py-2 text-right tabular-nums font-bold ${r.booked === 0 && r.leads >= 20 ? 'text-red-600' : 'text-gray-900'}`}>
                    {pct(r.booked, r.leads)}
                  </td>
                </tr>
              ))}
              {marketing.sourceRows.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-400">{t('noLeadsFound') || 'No leads in this period'}</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* §4 cost summary + spend ledger access */}
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-gray-500">
          <div>
            {spendCosts.active ? (() => {
              const meta = marketing.sourceRows.find((r) => normalizeSource(r.name) === normalizeSource('Meta Lead Gen'));
              const metaMedia = spendCosts.bySource['Meta Lead Gen'] || 0;
              const allIn = metaMedia + spendCosts.fee;
              const f = (n) => 'Rs ' + Math.round(n).toLocaleString();
              return (
                <span>
                  {(t('allInWithFee') || 'Meta all-in incl. agency fee')}: <b>{f(allIn)}</b>
                  {meta && meta.leads ? <> · {f(allIn / meta.leads)}/{t('leads').toLowerCase()}</> : null}
                  {meta && meta.booked ? <> · <b>{f(allIn / meta.booked)}/{(t('booked') || 'booked').toLowerCase()}</b></> : null}
                  {adSpend?.lastMetaSyncAt ? <> · {(t('lastSynced') || 'Meta synced')} {String(adSpend.lastMetaSyncAt).slice(0, 10)}</> : null}
                </span>
              );
            })() : (
              <span>{t('noSpendForPeriod') || 'Costs appear for month-or-longer periods once spend is recorded in the ledger'}</span>
            )}
          </div>
          <button onClick={() => setShowSpendEditor(true)} className="text-blue-600 font-semibold hover:underline">
            {t('editSpend') || 'Edit spend'}
          </button>
        </div>

        {/* §2 Meta ad-set breakdown */}
        <h4 className="mt-6 mb-1 font-semibold text-gray-700 text-sm">{t('adSetBreakdown') || 'Meta ad-set breakdown'}</h4>
        <p className="text-[11px] text-gray-400 mb-2">
          {t('adSetMetaNote') || 'Spend and Meta Leads come from the ad account for the full period (message ads count conversations started); CRM columns — visits and booked — attribute only from 29 Jun 2026 onward'}
        </p>
        {adsetDisplayRows.length === 0 ? (
          <div className="text-sm text-gray-400 py-3">{t('noAdsetData') || 'No ad-set data in this period'}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-gray-200 text-left">
                  <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase">{t('adSet') || 'Ad Set'}</th>
                  <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase text-right">{t('spend') || 'Spend'}</th>
                  <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase text-right">{t('metaLeads') || 'Meta Leads'}</th>
                  <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase text-right">{t('crmLeads') || 'CRM Leads'}</th>
                  <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase text-right">{t('rsPerLead') || 'Rs/Lead'}</th>
                  <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase text-right">{t('siteVisits') || 'Site Visits'}</th>
                  <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase text-right">{t('visitRate') || 'Visit rate'}</th>
                  <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase text-right">{t('booked') || 'Booked'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {adsetDisplayRows.map((r) => (
                  <tr key={r.name}>
                    <td className="px-3 py-2 text-gray-800 text-xs md:text-sm">{r.name}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                      {spendCosts.adsetBySet[r.name] ? 'Rs ' + Math.round(spendCosts.adsetBySet[r.name]).toLocaleString() : '—'}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {(() => {
                        // A message ad can pick up a stray lead action; showing
                        // only it would hide hundreds of conversations behind a
                        // "1". Always show both kinds when both exist.
                        const ml = spendCosts.adsetLeads[r.name] || 0;
                        const mc = spendCosts.adsetConvos[r.name] || 0;
                        const parts = [];
                        if (ml) parts.push(ml.toLocaleString());
                        if (mc) parts.push(mc.toLocaleString() + ' ' + (t('convosShort') || 'msgs'));
                        return parts.length ? parts.join(' + ') : '—';
                      })()}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.leads}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-800">
                      {(() => {
                        const sp = spendCosts.adsetBySet[r.name] || 0;
                        const res = (spendCosts.adsetLeads[r.name] || 0) + (spendCosts.adsetConvos[r.name] || 0);
                        return sp && res ? 'Rs ' + Math.round(sp / res).toLocaleString() : '—';
                      })()}
                    </td>
                    <td className={`px-3 py-2 text-right tabular-nums font-bold ${r.visits === 0 && r.leads >= 20 ? 'text-red-600' : 'text-gray-900'}`}>{r.visits}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-500">{pct(r.visits, r.leads)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.booked}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* §3 Month-by-month trend, selectable source */}
        <div className="mt-6 mb-2 flex items-end justify-between gap-2 flex-wrap">
          <div>
            <h4 className="font-semibold text-gray-700 text-sm">{t('monthTrend') || 'Month-by-month trend'}</h4>
            <p className="text-[11px] text-gray-400">
              {t('trendNote') || 'Last 12 months by lead creation month — independent of the period filter above'}
            </p>
          </div>
          <Dropdown className="w-auto">
            <DropdownTrigger>
              {/* The popover mirrors the trigger's width (inline style — width
                  classes on the popover are ignored), so the fix for truncated
                  source names is a wider trigger, not a wider popover. */}
              <button className="min-w-[230px] bg-white hover:bg-slate-50 border border-gray-200 rounded-lg text-sm px-3 h-[36px] focus:ring-2 focus:ring-blue-500 outline-none flex justify-between items-center text-left shadow-sm transition-colors">
                <span className="font-medium text-gray-700 truncate">
                  {marketingSource === 'All' ? (t('allSources') || 'All Sources') : marketingSource}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
              </button>
            </DropdownTrigger>
            <DropdownPopover>
              <DropdownMenu
                className="!max-h-72"
                selectedKeys={new Set([marketingSource])}
                selectionMode="single"
                onSelectionChange={(keys) => setMarketingSource(Array.from(keys)[0])}
              >
                <DropdownItem id="All">{t('allSources') || 'All Sources'}</DropdownItem>
                {marketing.allSourceNames.map((s) => (
                  <DropdownItem key={s} id={s}>{s}</DropdownItem>
                ))}
              </DropdownMenu>
            </DropdownPopover>
          </Dropdown>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-gray-200 text-left">
                <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase">{t('month') || 'Month'}</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase text-right">{t('spend') || 'Spend'}</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase text-right">{t('leads')}</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase text-right">{t('rsPerLead') || 'Rs/Lead'}</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase text-right">{t('siteVisits') || 'Site Visits'}</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase text-right">{t('booked') || 'Booked'}</th>
                <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase text-right">{t('conversionRate') || 'Conversion'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {marketing.trendRows.map((m) => (
                // A month with real volume and zero bookings is the exact failure
                // this table exists to surface — tint it so nobody scrolls past.
                <tr key={m.key} className={m.leads >= 20 && m.booked === 0 ? 'bg-red-50/70' : ''}>
                  <td className="px-3 py-2 font-medium text-gray-700">{m.label}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                    {trendSpendFor(m.key) ? 'Rs ' + Math.round(trendSpendFor(m.key)).toLocaleString() : '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{m.leads.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-800">
                    {trendSpendFor(m.key) && m.leads ? 'Rs ' + Math.round(trendSpendFor(m.key) / m.leads).toLocaleString() : '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{m.visits}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{m.booked}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-500">{pct(m.booked, m.leads)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Why we lose ── */}
      <div className="bg-white p-4 md:p-5 rounded-xl border shadow-sm">
        <h3 className="font-bold text-gray-800 flex items-center gap-2 text-sm md:text-base">
          <AlertTriangle className="w-4 h-4" /> {t('whyWeLose') || 'Why we lose'}
        </h3>
        <p className="text-[11px] text-gray-400 mb-3">
          {t('whyWeLoseNote') || 'Leads created in the selected period that ended in Lost, by the reason recorded when they were closed'}
        </p>

        {/* The two win rates side by side — the gap between them is the cost
            of counting spam and silent numbers as sales failures. */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="rounded-xl border border-gray-200 bg-slate-50/60 p-3">
            <p className="text-[11px] text-gray-500 font-medium">{t('rawWinRate') || 'Win rate, all inquiries'}</p>
            <p className="text-lg font-bold text-gray-700 tabular-nums">{lossAnalysis.rawRate.toFixed(1)}%</p>
          </div>
          <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-3">
            <p className="text-[11px] text-blue-700 font-medium">{t('qualifiedWinRate') || 'Win rate, qualified deals'}</p>
            <p className="text-lg font-bold text-blue-800 tabular-nums">
              {lossAnalysis.recorded ? lossAnalysis.qualifiedRate.toFixed(1) + '%' : '—'}
            </p>
            <p className="text-[10px] text-blue-600/80 leading-tight mt-0.5">
              {lossAnalysis.won} {t('wonShort') || 'won'} / {lossAnalysis.won + lossAnalysis.realLosses} {t('closedShort') || 'closed'}
            </p>
          </div>
        </div>

        {lossAnalysis.rows.length === 0 ? (
          <div className="text-sm text-gray-400 py-3">
            {t('noLossReasons') || 'No loss reasons recorded yet — they are collected from now on, each time a lead is moved to Lost.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-gray-200 text-left">
                  <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase">{t('reason') || 'Reason'}</th>
                  <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase text-right">{t('leads') || 'Leads'}</th>
                  <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase text-right">{t('shareOfLost') || 'Share'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lossAnalysis.rows.map((r) => (
                  <tr key={r.id} className={r.klass === 'disqualified' ? 'text-gray-400' : ''}>
                    <td className="px-3 py-2">
                      {t('loss_' + r.id) || r.label}
                      {r.klass === 'disqualified' && (
                        <span className="ml-2 text-[10px] uppercase tracking-wide text-gray-400">
                          {t('notADeal') || 'not a deal'}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{r.count}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-500">
                      {pct(r.count, lossAnalysis.recorded)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {lossAnalysis.unrecorded > 0 && (
          <p className="text-[11px] text-amber-600 mt-2 leading-snug">
            {lossAnalysis.unrecorded} {t('lostWithoutReason') || 'lead(s) closed before reasons were collected — excluded from both the table and the qualified rate.'}
          </p>
        )}
      </div>

      {/* ── §5 Bookings: same month across years (banquet system) ── */}
      <div className="bg-white p-4 md:p-5 rounded-xl border shadow-sm">
        <div className="flex items-end justify-between gap-2 flex-wrap mb-3">
          <div>
            <h3 className="font-bold text-gray-800 flex items-center gap-2 text-sm md:text-base">
              <Banknote className="w-4 h-4" /> {t('yoyBookings') || 'Bookings — same month across years'}
            </h3>
            <p className="text-[11px] text-gray-400">
              {t('yoyNote') || 'A young month reads low because upsells land as the event nears — compare Decembers to Decembers, never a running month to a finished year'}
            </p>
          </div>
          <Dropdown className="w-auto">
            <DropdownTrigger>
              {/* The popover mirrors the trigger's width (inline style, so
                  width classes on the popover are ignored) — the trigger must
                  be as wide as the 3-column month grid needs. */}
              <button className="min-w-[248px] bg-white hover:bg-slate-50 border border-gray-200 rounded-lg text-sm px-3 h-[36px] focus:ring-2 focus:ring-blue-500 outline-none flex justify-between items-center text-left shadow-sm transition-colors">
                <span className="font-medium text-gray-700">{monthNames[Number(yoyMonth) - 1]}</span>
                <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
              </button>
            </DropdownTrigger>
            {/* Month grid, not a 12-item scroll list: all twelve visible at
                once, calendar-style, one click. */}
            <DropdownPopover>
              <DropdownMenu
                className="!max-h-none grid grid-cols-3 gap-0.5 p-1.5"
                selectedKeys={new Set([yoyMonth])}
                selectionMode="single"
                onSelectionChange={(k) => setYoyMonth(Array.from(k)[0])}
              >
                {monthNamesShort.map((nm, i) => (
                  <DropdownItem
                    key={nm}
                    id={String(i + 1).padStart(2, '0')}
                    className="justify-center !mx-0 !px-2 text-center"
                  >
                    {nm}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </DropdownPopover>
          </Dropdown>
        </div>
        {yoy.length === 0 ? (
          <div className="h-24 flex items-center justify-center text-sm text-gray-400">
            {t('noDemandData') || 'No bookings for this month'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-gray-200 text-left">
                  <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase">{t('year') || 'Year'}</th>
                  <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase text-right">{t('events') || 'Events'}</th>
                  <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase text-right">{t('avgValue') || 'Avg value'}</th>
                  <th className="px-3 py-2 text-xs font-semibold text-gray-600 uppercase text-right">{t('totalValue') || 'Total'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {yoy.map((r) => (
                  <tr key={r.year}>
                    <td className="px-3 py-2 font-medium text-gray-800">
                      {r.year}
                      {r.maturing && (
                        <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 uppercase">
                          {t('stillMaturing') || 'still maturing'}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.events}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(r.avg)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold">{formatCurrency(r.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-2 text-[11px] text-gray-400">
          {t('invoiceFloorNote') || 'Values are banquet-system invoice totals. If add-on packages are invoiced separately there, these figures are floors, not totals.'}
        </p>
      </div>

      <SpendEditor
        open={showSpendEditor}
        onClose={() => setShowSpendEditor(false)}
        ledger={adSpend}
        onSaved={(v) => { cachedAdSpend = v || { months: {} }; setAdSpend(cachedAdSpend); }}
      />

      {/* ── Sales Team Summary Card ─────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-md overflow-hidden">

        {/* ── Header ── */}
        <div className="p-4 md:p-5 border-b border-gray-100">
          <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
            {/* Title */}
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-600 rounded-xl border border-blue-100 shadow-sm shrink-0">
                <BarChart3 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-base md:text-lg">{t('salesTeamSummary')}</h3>
                <p className="text-xs text-gray-500 mt-0.5">{t('activityAndBookingsBetween') || 'Activity, bookings & new leads within selected dates'}</p>
              </div>
            </div>

            {/* Controls */}
            <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 w-full xl:w-auto">

              {/* Date Pickers Container */}
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 shadow-sm w-full sm:w-auto shrink-0">
                <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
                <div className="flex-1 sm:w-32 min-w-0">
                  <DatePicker
                    value={summaryFrom}
                    onChange={e => setSummaryFrom(e.target.value)}
                    className="border-0 shadow-none bg-transparent !p-0 min-h-0 !h-auto text-xs sm:text-sm font-medium"
                    fullWidth
                  />
                </div>
                <span className="text-gray-300 text-xs shrink-0">→</span>
                <div className="flex-1 sm:w-32 min-w-0">
                  <DatePicker
                    value={summaryTo}
                    onChange={e => setSummaryTo(e.target.value)}
                    className="border-0 shadow-none bg-transparent !p-0 min-h-0 !h-auto text-xs sm:text-sm font-medium"
                    fullWidth
                  />
                </div>
              </div>

              {/* Quick Actions & Total Badge */}
              <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar pb-1 sm:pb-0 w-full sm:w-auto shrink-0">
                <div className="flex bg-gray-100 p-1 rounded-xl shrink-0">
                  <button
                    onClick={() => {
                      const today = new Date();
                      const yyyy = today.getFullYear();
                      const mm = String(today.getMonth() + 1).padStart(2, '0');
                      const dd = String(today.getDate()).padStart(2, '0');
                      const formatted = `${yyyy}-${mm}-${dd}`;
                      setSummaryFrom(formatted);
                      setSummaryTo(formatted);
                    }}
                    className="text-[11px] font-bold text-gray-600 hover:text-gray-900 bg-transparent hover:bg-white px-3 py-1.5 rounded-lg transition-all"
                  >
                    {t('todayBtn')}
                  </button>
                  <button
                    onClick={() => { const d = getMonthDefaults(); setSummaryFrom(d.from); setSummaryTo(d.to); }}
                    className="text-[11px] font-bold text-gray-600 hover:text-gray-900 bg-transparent hover:bg-white px-3 py-1.5 rounded-lg transition-all"
                  >
                    {t('thisMonth')}
                  </button>
                  <button
                    onClick={() => {
                      const y = new Date().getFullYear();
                      setSummaryFrom(`${y}-01-01`);
                      setSummaryTo(`${y}-12-31`);
                    }}
                    className="text-[11px] font-bold text-gray-600 hover:text-gray-900 bg-transparent hover:bg-white px-3 py-1.5 rounded-lg transition-all"
                  >
                    {t('thisYear')}
                  </button>
                  <button
                    onClick={() => {
                      // Wide open on both ends: covers the 2023 CRM epoch and
                      // anything imported with older dates.
                      setSummaryFrom('2000-01-01');
                      setSummaryTo(fmtDay(new Date()));
                    }}
                    className="text-[11px] font-bold text-gray-600 hover:text-gray-900 bg-transparent hover:bg-white px-3 py-1.5 rounded-lg transition-all"
                  >
                    {t('allTime') || 'All Time'}
                  </button>
                </div>

                {/* Total badge */}
                <button
                  onClick={() => { setSummaryModalType('leads-all'); setVisibleCount(50); }}
                  className="flex items-center gap-2 bg-white border border-gray-200 hover:border-blue-300 hover:shadow-sm rounded-xl px-3 py-1.5 transition-all shrink-0 ml-auto sm:ml-0"
                >
                  <div className="text-[10px] font-bold text-gray-500 uppercase tracking-widest hidden sm:block">{t('totalLeads')}</div>
                  <div className="text-[10px] font-bold text-gray-500 uppercase tracking-widest sm:hidden">{t('total')}</div>
                  <div className="text-sm font-black text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md">{crmSummary.totalLeads}</div>
                </button>
              </div>

            </div>
          </div>

          {/* Summary pills */}
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-50">
            {[
              { label: t('activeLabel'), val: crmSummary.activeLeads.length, color: 'bg-blue-50 text-blue-500 border-blue-200', type: 'leads-active' },
              { label: t('won'), val: crmSummary.salespeople.reduce((s, sp) => s + sp.won.length, 0), color: 'bg-green-50 text-green-500 border-green-200', type: 'stage-Won' },
              { label: t('callsOverdue'), val: crmSummary.overdueCallLeads.length, color: 'bg-red-50 text-red-500 border-red-200', type: 'calls-overdue' },
              { label: t('callsToday'), val: crmSummary.todayCallLeads.length, color: 'bg-amber-50 text-amber-500 border-amber-200', type: 'calls-today' },
              { label: t('visits'), val: crmSummary.siteVisitLeads.length, color: 'bg-purple-50 text-purple-500 border-purple-200', type: 'visits-all' },
            ].map(pill => pill.type ? (
              <button key={pill.label} onClick={() => { setSummaryModalType(pill.type); setVisibleCount(50); }}
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold border ${pill.color} hover:shadow-sm transition-all`}>
                {pill.label}: <span className="font-black">{pill.val}</span>
              </button>
            ) : (
              <span key={pill.label} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold border ${pill.color}`}>
                {pill.label}: <span className="font-black">{pill.val}</span>
              </span>
            ))}
          </div>
        </div>

        {/* ── Table ── */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead>
              {/* Group labels */}
              <tr className="text-[10px] font-black uppercase tracking-widest border-b">
                <th className="px-5 py-2 text-left text-gray-400 w-52 sticky left-0 bg-white z-20"></th>
                <th colSpan={9} className="py-2 text-center text-blue-500 bg-blue-50 border-x border-blue-100">{t('leads').toUpperCase()}</th>
                <th colSpan={3} className="py-2 text-center text-rose-500 bg-red-50 border-x border-red-100">{t('callsHeader')}</th>
                <th colSpan={3} className="py-2 text-center text-purple-500 bg-purple-50 border-x border-purple-100">{t('scheduledSiteVisits')}</th>
                <th className="py-2 text-right px-5 text-gray-400">{t('revenue').toUpperCase()}</th>
              </tr>
              {/* Column headers */}
              <tr className="text-[11px] font-bold text-gray-500 border-b bg-gray-50">
                <th className="px-5 py-2.5 text-left sticky left-0 bg-gray-50 z-20 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] border-r border-gray-100">{t('salesperson')}</th>
                <th className="px-3 py-2.5 text-center bg-blue-50/60 border-l border-blue-100 text-gray-600">{t('total')}</th>
                <th className="px-3 py-2.5 text-center bg-blue-50/60 text-blue-600">{t('activeLabel')}</th>
                <th className="px-3 py-2.5 text-center bg-blue-50/60 text-indigo-500">{t('newLabel')}</th>
                <th className="px-3 py-2.5 text-center bg-blue-50/60 text-cyan-600">{t('contacted')}</th>
                <th className="px-3 py-2.5 text-center bg-blue-50/60 text-sky-600">{t('quoted')}</th>
                <th className="px-3 py-2.5 text-center bg-blue-50/60 text-purple-600">{t('visited')}</th>
                <th className="px-3 py-2.5 text-center bg-blue-50/60 text-emerald-600">{t('won')}</th>
                <th className="px-3 py-2.5 text-center bg-blue-50/60 text-red-500">{t('lost')}</th>
                <th className="px-3 py-2.5 text-center bg-blue-50/60 border-r border-blue-100 text-indigo-600">{t('convRateAbbr')}</th>
                <th className="px-3 py-2.5 text-center bg-red-50/60 border-l border-red-100 text-red-500">{t('overdue')}</th>
                <th className="px-3 py-2.5 text-center bg-red-50/20 text-amber-600">{t('todayBtn')}</th>
                <th className="px-3 py-2.5 text-center bg-red-50/20 border-r border-red-100 text-emerald-600">{t('upcoming')}</th>
                <th className="px-3 py-2.5 text-center bg-purple-50/60 border-l border-purple-100 text-red-500">{t('overdue')}</th>
                <th className="px-3 py-2.5 text-center bg-purple-50/20 text-amber-600">{t('todayBtn')}</th>
                <th className="px-3 py-2.5 text-center bg-purple-50/20 border-r border-purple-100 text-emerald-600">{t('upcoming')}</th>
                <th className="px-5 py-2.5 text-right text-gray-600">{t('revenue')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {crmSummary.salespeople.length === 0 ? (
                <tr>
                  <td colSpan={12} className="px-5 py-12 text-center">
                    <div className="text-gray-300 mb-2 flex justify-center"><BarChart3 className="w-8 h-8 opacity-50" /></div>
                    <div className="text-gray-500 text-sm font-medium">{t('noLeadsSelectedPeriod')}</div>
                    <div className="text-gray-400 text-xs mt-1">{t('tryAdjustingDate')}</div>
                  </td>
                </tr>
              ) : crmSummary.salespeople.map((sp, i) => {
                const avatarColors = [
                  'from-blue-500 to-indigo-600',
                  'from-emerald-500 to-teal-600',
                  'from-amber-500 to-orange-600',
                  'from-purple-500 to-violet-600',
                  'from-pink-500 to-rose-600',
                  'from-cyan-500 to-sky-600',
                ];
                const gradient = avatarColors[i % avatarColors.length];
                const initials = (sp.name || '?').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
                const openSp = (type) => { setSummaryModalType(`sp:${sp.name}:${type}`); setVisibleCount(50); };
                const maxLeads = Math.max(...crmSummary.salespeople.map(s => s.total.length), 1);
                const barWidth = Math.round((sp.total.length / maxLeads) * 100);

                return (
                  <motion.tr
                    key={sp.name}
                    className="group hover:bg-slate-50 transition-colors"
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05, duration: 0.3 }}
                  >

                    {/* Name + mini bar */}
                    <td className="px-5 py-3 sticky left-0 bg-white z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)] group-hover:bg-slate-50 transition-colors border-r border-gray-50">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center text-white text-xs font-black flex-shrink-0 shadow`}>
                          {initials}
                        </div>
                        <div className="min-w-0">
                          <div className="font-bold text-gray-900 text-sm truncate leading-tight">{sp.name}</div>
                          <div className="mt-1 flex items-center gap-1.5">
                            <div className="flex-1 bg-gray-100 rounded-full h-1.5 w-20">
                              <div className={`h-full rounded-full bg-gradient-to-r ${gradient}`} style={{ width: `${barWidth}%` }} />
                            </div>
                            <span className="text-[10px] text-gray-400">{sp.total.length} {t('leads')}</span>
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Total */}
                    <td className="px-3 py-3 text-center bg-blue-50/30 border-l border-blue-100">
                      <button onClick={() => openSp('total')} className="text-sm font-black text-gray-800 hover:text-blue-600 px-2 py-0.5 rounded-lg hover:bg-blue-100 transition-colors">{sp.total.length}</button>
                    </td>
                    {/* Active */}
                    <td className="px-3 py-3 text-center bg-blue-50/30">
                      <button onClick={() => openSp('active')} className="text-sm font-bold text-blue-500 hover:bg-blue-100 px-2 py-0.5 rounded-lg transition-colors">{sp.active.length}</button>
                    </td>
                    {/* New */}
                    <td className="px-3 py-3 text-center bg-blue-50/30">
                      <button onClick={() => openSp('new')} className="text-sm font-bold text-indigo-500 hover:bg-blue-100 px-2 py-0.5 rounded-lg transition-colors">{sp.newLeads.length}</button>
                    </td>
                    {/* Contacted */}
                    <td className="px-3 py-3 text-center bg-blue-50/30">
                      <button onClick={() => openSp('contacted')} className="text-sm font-bold text-cyan-600 hover:bg-blue-100 px-2 py-0.5 rounded-lg transition-colors">{sp.contacted.length}</button>
                    </td>
                    {/* Quoted */}
                    <td className="px-3 py-3 text-center bg-blue-50/30">
                      <button onClick={() => openSp('quoted')} className="text-sm font-bold text-sky-600 hover:bg-blue-100 px-2 py-0.5 rounded-lg transition-colors">{sp.quoted.length}</button>
                    </td>
                    {/* Visited */}
                    <td className="px-3 py-3 text-center bg-blue-50/30">
                      <button onClick={() => openSp('siteVisited')} className="text-sm font-bold text-purple-600 hover:bg-blue-100 px-2 py-0.5 rounded-lg transition-colors">{sp.siteVisited.length}</button>
                    </td>
                    {/* Won */}
                    <td className="px-3 py-3 text-center bg-blue-50/30">
                      <button onClick={() => openSp('won')} className="text-sm font-bold text-emerald-400 hover:bg-emerald-50 px-2 py-0.5 rounded-lg transition-colors">{sp.won.length}</button>
                    </td>
                    {/* Lost */}
                    <td className="px-3 py-3 text-center bg-blue-50/30">
                      <button onClick={() => openSp('lost')} className="text-sm font-bold text-red-400 hover:bg-red-50 px-2 py-0.5 rounded-lg transition-colors">{sp.lost.length}</button>
                    </td>
                    {/* Conv% with mini bar */}
                    <td className="px-3 py-3 text-center bg-blue-50/30 border-r border-blue-100">
                      <div className="flex flex-col items-center gap-1">
                        <span className={`text-[11px] font-black px-2 py-0.5 rounded-full ${sp.convRate >= 25 ? 'bg-emerald-100 text-emerald-500' :
                            sp.convRate >= 10 ? 'bg-amber-100 text-amber-500' :
                              'bg-red-100 text-red-400'
                          }`}>{sp.convRate}%</span>
                        <div className="w-12 bg-gray-100 rounded-full h-1">
                          <div className={`h-full rounded-full ${sp.convRate >= 25 ? 'bg-emerald-500' : sp.convRate >= 10 ? 'bg-amber-400' : 'bg-red-400'
                            }`} style={{ width: `${Math.min(sp.convRate * 2, 100)}%` }} />
                        </div>
                      </div>
                    </td>

                    {/* Calls Overdue */}
                    <td className="px-3 py-3 text-center border-l border-red-100">
                      {sp.calls.overdue.length > 0 ? (
                        <button onClick={() => openSp('calls-overdue')}
                          className="inline-flex items-center gap-1 text-xs font-bold text-red-400 bg-red-50 border border-red-200 hover:bg-red-100 px-2.5 py-1 rounded-full transition-colors">
                          <AlertTriangle className="w-3 h-3" />{sp.calls.overdue.length}
                        </button>
                      ) : <span className="text-gray-200">—</span>}
                    </td>
                    {/* Calls Today */}
                    <td className="px-3 py-3 text-center">
                      {sp.calls.today.length > 0 ? (
                        <button onClick={() => openSp('calls-today')}
                          className="text-xs font-bold text-amber-400 bg-amber-50 border border-amber-200 hover:bg-amber-100 px-2.5 py-1 rounded-full transition-colors">
                          {sp.calls.today.length}
                        </button>
                      ) : <span className="text-gray-200">—</span>}
                    </td>
                    {/* Calls Upcoming */}
                    <td className="px-3 py-3 text-center border-r border-red-100">
                      {sp.calls.upcoming.length > 0 ? (
                        <button onClick={() => openSp('calls-upcoming')}
                          className="text-xs font-bold text-emerald-400 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 px-2.5 py-1 rounded-full transition-colors">
                          {sp.calls.upcoming.length}
                        </button>
                      ) : <span className="text-gray-200">—</span>}
                    </td>

                    {/* Visits Overdue */}
                    <td className="px-3 py-3 text-center border-l border-purple-100 bg-purple-50/20">
                      {sp.visits.overdue.length > 0 ? (
                        <button onClick={() => openSp('visits-overdue')}
                          className="text-xs font-bold text-red-400 bg-red-50 border border-red-200 hover:bg-red-100 px-2 py-0.5 rounded-full transition-colors">
                          {sp.visits.overdue.length}
                        </button>
                      ) : <span className="text-gray-200">—</span>}
                    </td>
                    {/* Visits Today */}
                    <td className="px-3 py-3 text-center bg-purple-50/20">
                      {sp.visits.today.length > 0 ? (
                        <button onClick={() => openSp('visits-today')}
                          className="text-xs font-bold text-amber-400 bg-amber-50 border border-amber-200 hover:bg-amber-100 px-2 py-0.5 rounded-full transition-colors">
                          {sp.visits.today.length}
                        </button>
                      ) : <span className="text-gray-200">—</span>}
                    </td>
                    {/* Visits Upcoming */}
                    <td className="px-3 py-3 text-center border-r border-purple-100 bg-purple-50/20">
                      {sp.visits.upcoming.length > 0 ? (
                        <button onClick={() => openSp('visits-upcoming')}
                          className="text-xs font-bold text-emerald-400 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 px-2 py-0.5 rounded-full transition-colors">
                          {sp.visits.upcoming.length}
                        </button>
                      ) : <span className="text-gray-200">—</span>}
                    </td>

                    {/* Revenue */}
                    <td className="px-5 py-3 text-right">
                      <span className="font-black text-gray-800">{formatCurrency(sp.revenue)}</span>
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>

            {/* Totals footer */}
            {crmSummary.salespeople.length > 0 && (
              <tfoot>
                <tr className="bg-gray-50 border-t-2 border-gray-200 text-gray-600">
                  <td className="px-5 py-3 font-black text-sm sticky left-0 bg-gray-50 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] border-r border-gray-200">{t('teamTotal')}</td>
                  <td className="px-3 py-3 text-center border-l border-gray-200">
                    <button onClick={() => { setSummaryModalType('leads-all'); setVisibleCount(50); }} className="font-black text-gray-600 hover:text-blue-500 transition-colors">{crmSummary.totalLeads}</button>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <button onClick={() => { setSummaryModalType('leads-active'); setVisibleCount(50); }} className="font-black text-blue-500 hover:text-blue-600 transition-colors">{crmSummary.activeLeads.length}</button>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <button onClick={() => { setSummaryModalType('stage-New'); setVisibleCount(50); }} className="font-black text-indigo-500 hover:text-indigo-600 transition-colors">{crmSummary.salespeople.reduce((s, sp) => s + sp.newLeads.length, 0)}</button>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <button onClick={() => { setSummaryModalType('stage-Contacted'); setVisibleCount(50); }} className="font-black text-cyan-600 hover:text-cyan-700 transition-colors">{crmSummary.salespeople.reduce((s, sp) => s + sp.contacted.length, 0)}</button>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <button onClick={() => { setSummaryModalType('stage-Quoted'); setVisibleCount(50); }} className="font-black text-sky-600 hover:text-sky-700 transition-colors">{crmSummary.salespeople.reduce((s, sp) => s + sp.quoted.length, 0)}</button>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <button onClick={() => { setSummaryModalType('stage-Site Visited'); setVisibleCount(50); }} className="font-black text-purple-600 hover:text-purple-700 transition-colors">{crmSummary.salespeople.reduce((s, sp) => s + sp.siteVisited.length, 0)}</button>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <button onClick={() => { setSummaryModalType('stage-Won'); setVisibleCount(50); }} className="font-black text-emerald-400 hover:text-emerald-500 transition-colors">{crmSummary.salespeople.reduce((s, sp) => s + sp.won.length, 0)}</button>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <button onClick={() => { setSummaryModalType('stage-Lost'); setVisibleCount(50); }} className="font-black text-red-400 hover:text-red-500 transition-colors">{crmSummary.salespeople.reduce((s, sp) => s + sp.lost.length, 0)}</button>
                  </td>
                  <td className="px-3 py-3 text-center border-r border-gray-200">
                    <span className="font-black text-indigo-400">
                      {crmSummary.totalLeads > 0 ? Math.round((crmSummary.salespeople.reduce((s, sp) => s + sp.won.length, 0) / crmSummary.totalLeads) * 100) : 0}%
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center border-l border-gray-200">
                    <button onClick={() => { setSummaryModalType('calls-overdue'); setVisibleCount(50); }} className="font-bold text-red-400 hover:text-red-500 transition-colors">{crmSummary.overdueCallLeads.length}</button>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <button onClick={() => { setSummaryModalType('calls-today'); setVisibleCount(50); }} className="font-bold text-amber-400 hover:text-amber-500 transition-colors">{crmSummary.todayCallLeads.length}</button>
                  </td>
                  <td className="px-3 py-3 text-center border-r border-gray-200">
                    <button onClick={() => { setSummaryModalType('calls-upcoming'); setVisibleCount(50); }} className="font-bold text-emerald-400 hover:text-emerald-500 transition-colors">{crmSummary.upcomingCallLeads.length}</button>
                  </td>
                  <td className="px-3 py-3 text-center border-l border-gray-200">
                    <button onClick={() => { setSummaryModalType('visits-overdue'); setVisibleCount(50); }} className="font-bold text-red-400 hover:text-red-500 transition-colors">{crmSummary.overdueVisits.length}</button>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <button onClick={() => { setSummaryModalType('visits-today'); setVisibleCount(50); }} className="font-bold text-amber-400 hover:text-amber-500 transition-colors">{crmSummary.todayVisits.length}</button>
                  </td>
                  <td className="px-3 py-3 text-center border-r border-gray-200">
                    <button onClick={() => { setSummaryModalType('visits-upcoming'); setVisibleCount(50); }} className="font-bold text-emerald-400 hover:text-emerald-500 transition-colors">{crmSummary.upcomingVisits.length}</button>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <span className="font-black text-gray-600">{formatCurrency(crmSummary.salespeople.reduce((s, sp) => s + sp.revenue, 0))}</span>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Third Row: Audit Logs, Bookings, Conversations */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent Audit Logs Section */}
        <div className="lg:col-span-1 bg-white rounded-xl border shadow-sm overflow-hidden flex flex-col h-[400px]">
          <div className="p-4 border-b bg-gray-50/50 flex items-center justify-between flex-shrink-0">
            <h3 className="font-bold text-gray-800 flex items-center gap-2 text-sm">
              <History className="w-4 h-4 text-blue-600" /> {t('recentAuditLogs') || 'Recent Audit Logs'}
            </h3>
            <Link
              to="/audit-logs"
              className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
            >
              {t('viewAll') || 'View All'} <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
          <div className="divide-y overflow-y-auto flex-1 custom-scrollbar">
            {logsLoading ? (
              <div className="p-4 space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex gap-3">
                    <Skeleton variant="circular" width={32} height={32} animation="wave" className="flex-shrink-0" />
                    <div className="flex-1 space-y-2 py-1">
                      <Skeleton width="40%" height={12} animation="wave" />
                      <Skeleton width="80%" height={10} animation="wave" />
                    </div>
                  </div>
                ))}
              </div>
            ) : recentAuditLogs.length === 0 ? (
              <div className="p-6 text-center text-gray-400 text-xs">
                {t('noAuditLogs') || 'No recent audit logs found'}
              </div>
            ) : (
              recentAuditLogs.map((log) => {
                const getActionDetails = (action, entity) => {
                  const details = {
                    text: action.replace(/_/g, ' ').toLowerCase(),
                    icon: <Sparkles className="w-3.5 h-3.5" />,
                    color: 'bg-gray-100 text-gray-600'
                  };

                  if (action.includes('CREATE')) {
                    details.text = 'created new';
                    details.icon = <PlusCircle className="w-3.5 h-3.5" />;
                    details.color = 'bg-emerald-100 text-emerald-600';
                  } else if (action.includes('UPDATE')) {
                    details.text = 'updated';
                    details.icon = <Edit className="w-3.5 h-3.5" />;
                    details.color = 'bg-blue-100 text-blue-600';
                  } else if (action.includes('DELETE')) {
                    details.text = 'deleted';
                    details.icon = <Trash2 className="w-3.5 h-3.5" />;
                    details.color = 'bg-red-100 text-red-600';
                  } else if (action.includes('CALL')) {
                    details.text = 'logged call for';
                    details.icon = <Phone className="w-3.5 h-3.5" />;
                    details.color = 'bg-indigo-100 text-indigo-600';
                  } else if (action.includes('NOTE')) {
                    details.text = 'added note to';
                    details.icon = <FileText className="w-3.5 h-3.5" />;
                    details.color = 'bg-amber-100 text-amber-600';
                  } else if (action.includes('PAYMENT')) {
                    details.text = 'recorded payment for';
                    details.icon = <DollarSign className="w-3.5 h-3.5" />;
                    details.color = 'bg-green-100 text-green-600';
                  }

                  return details;
                };

                const actionDetails = getActionDetails(log.action, log.entity);

                return (
                  <div key={log.id} className={`p-3 hover:bg-gray-50 transition-colors flex items-start gap-3 ${log.lead ? 'cursor-pointer' : ''}`} onClick={() => log.lead && navigate(`?leadId=${log.lead.id}`)}>
                    <div className={`${actionDetails.color} p-2 rounded-lg flex-shrink-0 mt-0.5`}>
                      {actionDetails.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[13px] font-bold text-gray-900">{log.user?.username || 'System'}</span>
                        <span className="text-[12px] text-gray-500">{actionDetails.text}</span>
                        <span className="text-[12px] font-semibold text-blue-600 uppercase tracking-tight">{log.entity}</span>
                      </div>
                      {log.lead && (
                        <p className="text-[11px] mt-0.5 text-gray-600 flex items-center gap-1">
                          <User className="w-3 h-3 text-gray-400" />
                          <span className="font-bold text-gray-800">{log.lead.contact.firstName}{log.lead.contact.lastName ? ' ' + log.lead.contact.lastName : ''}</span>
                          <span className="text-gray-500">({log.lead.contact.phone})</span>
                        </p>
                      )}
                    </div>
                    <div className="text-[10px] text-gray-400 text-right flex-shrink-0 leading-tight pt-1">
                      <div className="font-medium text-gray-500">
                        {formatDate(log.createdAt, {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </div>
                      <div className="opacity-70 mt-1">
                        {new Date(log.createdAt).toLocaleTimeString(language === 'ur' ? 'ur-PK' : 'en-US', {
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        })}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Upcoming Bookings Section */}
        <div className="lg:col-span-1 bg-white rounded-xl border shadow-sm overflow-hidden flex flex-col h-[400px]">
          <div className="p-4 border-b bg-gray-50/50 flex items-center justify-between flex-shrink-0">
            <h3 className="font-bold text-gray-800 flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-green-600" /> {t('upcomingBookings') || 'Upcoming Bookings'}
            </h3>
            <Link
              to="/calendar"
              className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 transition-colors"
            >
              {t('viewAll') || 'View All'} <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
          <div className="divide-y divide-gray-50 overflow-y-auto flex-1 custom-scrollbar">
            {bookingsLoading ? (
              <div className="p-4 space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex gap-3 items-center">
                    <Skeleton variant="rounded" width={48} height={48} animation="wave" className="flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <Skeleton width="60%" height={14} animation="wave" />
                      <Skeleton width="40%" height={12} animation="wave" />
                    </div>
                    <Skeleton width={60} height={24} variant="rounded" animation="wave" />
                  </div>
                ))}
              </div>
            ) : upcomingBookings.length === 0 ? (
              <div className="p-10 text-center">
                <Calendar className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                <p className="text-gray-400 text-xs">{t('noUpcomingBookings') || 'No upcoming bookings.'}</p>
              </div>

            ) : (
              upcomingBookings.map((booking) => {
                console.log("-----------BOOKINGS----------------")
                console.log("booking", booking);
                const shift = (booking.shift || booking.lead?.shift || 'Day').toLowerCase();
                const isDay = shift === 'day';
                return (
                  <div key={booking.id} className="p-3 hover:bg-gray-50 transition-colors flex items-start gap-3 cursor-pointer" onClick={() => setSelectedBookingForModal(booking)}>
                    <div className={`p-2 rounded-lg flex-shrink-0 mt-0.5 ${isDay ? 'bg-amber-100 text-amber-600' : 'bg-indigo-100 text-indigo-600'}`}>
                      {isDay ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[13px] font-bold text-gray-900">{booking.title}</span>
                        <span className="text-[12px] text-gray-500">for</span>
                        <span className="text-[12px] font-semibold text-blue-600 truncate">{booking.clientName || booking.lead?.clientName || 'Client'}</span>
                      </div>
                      <p className="text-[11px] mt-0.5 text-gray-600 flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-gray-400" />
                        <span className="font-medium text-gray-800">{booking.venue || 'TBD'}</span>
                      </p>
                    </div>
                    <div className="text-[10px] text-gray-400 text-right flex-shrink-0 leading-tight pt-1">
                      <div className="font-bold text-gray-500">
                        {formatDate(booking.startTime, {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </div>
                    </div>
                  </div>
                );
              })

            )}
          </div>
        </div>

        {/* Recent Conversations Section */}
        <div className="lg:col-span-1 bg-white rounded-xl border shadow-sm overflow-hidden flex flex-col h-[400px]">
          <div className="p-4 border-b bg-gray-50/50 flex items-center justify-between flex-shrink-0">
            <h3 className="font-bold text-gray-800 flex items-center gap-2 text-sm">
              <MessageCircle className="w-4 h-4 text-purple-600" /> {t('recentConversations') || 'Recent Conversations'}
            </h3>
            <Link
              to="/conversation-logs"
              className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
            >
              {t('viewAll') || 'View All'} <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
          <div className="divide-y overflow-y-auto flex-1 custom-scrollbar">
            {conversationsLoading ? (
              <div className="p-4 space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex gap-3">
                    <Skeleton variant="circular" width={40} height={40} animation="wave" className="flex-shrink-0" />
                    <div className="flex-1 space-y-2 py-1">
                      <Skeleton width="50%" height={14} animation="wave" />
                      <Skeleton width="90%" height={12} animation="wave" />
                    </div>
                  </div>
                ))}
              </div>
            ) : recentConversations.length === 0 ? (
              <div className="p-6 text-center text-gray-400 text-xs">
                {t('noRecentConversations') || 'No recent conversations.'}
              </div>
            ) : (
              recentConversations.map((convo) => {
                const getInitials = (name) => {
                  if (!name) return '?';
                  const parts = name.trim().split(/\s+/);
                  if (parts.length >= 2 && parts[1].length > 0) return (parts[0][0] + parts[1][0]).toUpperCase();
                  return name.charAt(0).toUpperCase();
                };

                return (
                  <div key={convo.id} className="p-3 hover:bg-gray-50 transition-colors flex items-start gap-3 cursor-pointer" onClick={() => navigate(`/conversation-logs?chatId=${convo.lead?.id}`)}>
                    <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-[11px] font-bold flex-shrink-0 mt-0.5">
                      {getInitials(convo.lead?.clientName)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-[13px] font-bold text-gray-900 truncate">{convo.lead?.clientName}</span>
                          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-indigo-50 text-indigo-600 flex-shrink-0">
                            {convo.lead?.manager || 'N/A'}
                          </span>
                        </div>
                        <span className="text-[10px] text-gray-400 flex-shrink-0">
                          {formatDate(convo.updatedAt, {
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                      </div>
                      <p className="text-[11px] text-gray-500 truncate mt-0.5 flex items-center gap-1.5">
                        {convo.hasUnreadMessages && (
                          <span className="w-1.5 h-1.5 bg-blue-600 rounded-full flex-shrink-0"></span>
                        )}
                        <span className="truncate">
                          {(() => {
                            const raw = convo.lastMessagePreview || convo.lastMessage;
                            if (!raw) return 'No messages yet';
                            try {
                              const parsed = JSON.parse(raw);
                              if (parsed.text) return parsed.text;
                              if (parsed.notes) return parsed.notes;
                              if (parsed.outcome) return parsed.outcome.replace(/_/g, ' ');
                              return raw;
                            } catch (e) {
                              return raw;
                            }
                          })()}
                        </span>
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>



      {/* AI Chat Interface */}
      <div className="bg-gradient-to-r from-indigo-900 to-purple-900 rounded-xl p-4 md:p-6 text-white">
        <div className="flex flex-col md:flex-row md:items-start gap-4">
          <div className="p-2 md:p-3 bg-white/10 rounded-xl w-fit">
            <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-yellow-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-base md:text-lg">{t('aiBusinessAssistant')}</h3>
            <p className="text-indigo-200 text-xs md:text-sm mt-1">
              {t('askAboutBusiness')}
            </p>

            {aiResponse && (
              <div className="mt-4 bg-white/10 rounded-lg p-3 md:p-4 text-xs md:text-sm leading-relaxed whitespace-pre-line">
                {aiResponse
                  .replace(/\*\*([^*]+)\*\*/g, '$1') // Remove ** markers (CSS will handle emphasis via context)
                  .replace(/\*([^*]+)\*/g, '$1')     // Remove single * markers
                  .split('\n').map((line, i) => (
                    <span key={i} className={line.startsWith('•') ? 'block ml-2' : 'block'}>
                      {line.includes(':') && !line.startsWith('•') ? (
                        <strong>{line}</strong>
                      ) : line}
                    </span>
                  ))
                }
              </div>
            )}

            <div className="mt-4 flex gap-2">
              <input
                type="text"
                className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-sm placeholder-indigo-300 outline-none focus:border-indigo-400"
                placeholder={t('typeQuestion')}
                value={aiQuery}
                onChange={(e) => setAiQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAiSubmit()}
              />
              <button
                onClick={handleAiSubmit}
                disabled={aiLoading}
                className="px-3 md:px-4 py-2 md:py-2.5 bg-indigo-600 hover:bg-indigo-500 rounded-lg font-medium text-xs md:text-sm disabled:opacity-50 flex items-center gap-1 md:gap-2"
              >
                {aiLoading ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">{t('askButton')}</span>
              </button>
            </div>

            <div className="mt-3 flex gap-2 flex-wrap">
              {[
                t('activeLeads'),
                t('staleLeads'),
                t('revenue')
              ].map((q) => (
                <button
                  key={q}
                  onClick={() => {
                    setAiQuery(q);
                    handleAiSubmit();
                  }}
                  className="text-[10px] md:text-xs bg-white/10 hover:bg-white/20 px-2 md:px-3 py-1 md:py-1.5 rounded-full"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {
        metrics.overduePayments.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 md:p-4">
              <h3 className="font-bold text-red-800 flex items-center gap-2 mb-3 text-sm md:text-base">
                <AlertTriangle className="w-4 h-4" /> {t('overduePayments')} ({metrics.overduePayments.length})
              </h3>
              <div className="space-y-2 max-h-32 md:max-h-40 overflow-y-auto">
                {metrics.overduePayments.slice(0, 5).map((lead) => (
                  <div
                    key={lead.id}
                    className="flex justify-between items-center text-xs md:text-sm bg-white p-2 rounded"
                  >
                    <span className="font-medium truncate">{lead.clientName}</span>
                    <span className="text-red-600 ml-2 flex-shrink-0">{formatCurrency(lead.totalDue)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
}
